const runtimeConfig = window.ROAMDATES_CONFIG || {};
const BUILD_STATIC_ONLY = import.meta.env.VITE_STATIC_ONLY === "true";
let sqliteHttpModulePromise = null;

async function loadSqliteHttpModule() {
  if (BUILD_STATIC_ONLY) {
    throw new Error("This build is static-only and does not include remote SQLite support");
  }
  if (!sqliteHttpModulePromise) {
    sqliteHttpModulePromise = import("sqlite-wasm-http");
  }
  return sqliteHttpModulePromise;
}

const DB_SQLITE_URL = new URL(runtimeConfig.dbUrl || "./data/attractions.sqlite3", window.location.href).href;
const DATA_JSON_URL = new URL(runtimeConfig.dataJsonUrl || "./data/attractions.json", window.location.href).href;
const DB_CACHE_BUST = "20260316-roamdates-v27";
const IS_LOOPBACK_HOST = /^(localhost|127(?:\.\d{1,3}){3}|\[::1\])$/i.test(window.location.hostname);

const DEFAULT_CITY_TIME_ZONE = "Asia/Tokyo";
const APP_NAME = "Roamdates";
const DEFAULT_CITY_ZOOM = 12;
const SETTINGS_STORAGE_KEY = "roamdates-settings-v1";
const SETTINGS_STORAGE_VERSION = 1;
const DEFAULT_CLUSTER_DISTANCE_METERS = 800;
const MAP_STYLE_URL = runtimeConfig.mapStyle || "https://tiles.openfreemap.org/styles/liberty";

const CLUSTER_ORDER = ["Attractions", "Nature", "Food", "Shopping", "Entertainment", "Practical"];

const CATEGORY_META = {
  "Scenic Landmarks & Historic Sites": { cluster: "Attractions", color: "#2f6bff" },
  "Museums": { cluster: "Attractions", color: "#7b93ff" },
  "Parks, Gardens, Beaches & Lakes": { cluster: "Nature", color: "#18a667" },
  "Mountains & Waterfalls": { cluster: "Nature", color: "#1e88a8" },
  "Famous Restaurants": { cluster: "Food", color: "#ff6a2a" },
  "Street Food Alleys": { cluster: "Food", color: "#ff8a1e" },
  "Outdoor Food Markets": { cluster: "Food", color: "#ff7a33" },
  "Dessert Cafés, Bakeries & Sweets": { cluster: "Food", color: "#ff9b54" },
  "Traditional Markets & Artisan Crafts": { cluster: "Shopping", color: "#d99412" },
  "Outlet Malls & Shopping Districts": { cluster: "Shopping", color: "#f1b62e" },
  "Theme Parks": { cluster: "Entertainment", color: "#7f52ff" },
  "Zoos & Aquariums": { cluster: "Entertainment", color: "#5f6cff" },
  "Bars, Nightlife & Casinos": { cluster: "Entertainment", color: "#bc4cff" },
  "Seasonal & Holiday Events": { cluster: "Entertainment", color: "#9347ff" },
  "Live Music & Concept Cafés": { cluster: "Entertainment", color: "#d35dff" },
  "Private Restrooms": { cluster: "Practical", color: "#667a93" },
};

const CATEGORY_ORDER = [
  "Scenic Landmarks & Historic Sites",
  "Museums",
  "Parks, Gardens, Beaches & Lakes",
  "Mountains & Waterfalls",
  "Famous Restaurants",
  "Street Food Alleys",
  "Outdoor Food Markets",
  "Dessert Cafés, Bakeries & Sweets",
  "Traditional Markets & Artisan Crafts",
  "Outlet Malls & Shopping Districts",
  "Theme Parks",
  "Zoos & Aquariums",
  "Bars, Nightlife & Casinos",
  "Seasonal & Holiday Events",
  "Live Music & Concept Cafés",
  "Private Restrooms",
];

const CLUSTER_COLORS = Object.fromEntries(
  CLUSTER_ORDER.map((clusterName) => {
    const first = Object.values(CATEGORY_META).find((meta) => meta.cluster === clusterName);
    return [clusterName, first?.color || "#6b7280"];
  }),
);

const WALKING_DISTANCE_METERS = 1000;
const WALKING_CLUSTER_DISTANCE_OPTIONS = [300, 800, 1300];
const UFUZZY_INFO_THRESHOLD = 10000;
const UFUZZY_OUT_OF_ORDER_TERMS = 1;

const ROUTE_PROVIDER_META = {
  google: { label: "Google Maps" },
  apple: { label: "Apple Maps" },
  amap: { label: "AMap" },
  amap_mobile: { label: "AMap Mobile" },
};

const TRAVEL_MODE_LABELS = {
  walking: "Walking",
  driving: "Driving",
  transit: "Transit",
  bicycling: "Bicycling",
};

const TRAVEL_MODES_BY_PROVIDER = {
  google: ["transit", "walking", "driving", "bicycling"],
  apple: ["transit", "walking", "driving"],
  amap: ["transit", "walking", "driving"],
  amap_mobile: ["transit", "walking", "driving"],
};

const HOURS_FILTERS = {
  all: { label: "Any time" },
  now: { label: "Open now" },
  today: { label: "Open today" },
  tomorrow: { label: "Open tomorrow" },
};

const SEARCH_SHORTCUTS = ["Trad-Style", "Modern-Style", "Hidden-Gem", "Tourist-Hotspot"];

const WEEKDAY_BITS = {
  Mon: 1,
  Tue: 2,
  Wed: 4,
  Thu: 8,
  Fri: 16,
  Sat: 32,
  Sun: 64,
};

const MOBILE_QUERY = window.matchMedia("(max-width: 980px)");

const state = {
  dbWorker: null,
  activeCity: null,
  cityMetaByName: new Map(),
  selectedCategories: new Set(),
  enabledCategories: new Set(),
  markersLayer: null,
  locationLayer: null,
  clusterOverlayLayer: null,
  requestToken: 0,
  userLocation: null,
  routeModeEnabled: false,
  routeProvider: detectDefaultRouteProvider(),
  routePoints: [],
  hypotheticalModeEnabled: false,
  hypotheticalMarker: null,
  hypotheticalPoint: null,
  markerById: new Map(),
  visibleRows: [],
  searchQuery: "",
  scheduleFilter: "all",
  currentCityClock: null,
  nearbyModeEnabled: false,
  clusterVisualizationEnabled: false,
  clusterDistanceMeters: DEFAULT_CLUSTER_DISTANCE_METERS,
  locationWatchId: null,
  offlineReady: false,
  serviceWorkerRegistration: null,
  activeDetailId: null,
  detailRequestToken: 0,
  backendMode: "sqlite",
  localRows: null,
  pendingActiveCity: null,
  pendingSelectedCategories: null,
  pendingTravelMode: null,
  pendingNearbyModeEnabled: false,
  pendingMapView: null,
};


const citySelect = document.getElementById("citySelect");
const categoryList = document.getElementById("categoryList");
const markerCountEl = document.getElementById("markerCount");
const bytesReadEl = document.getElementById("bytesRead");
const statusTextEl = document.getElementById("statusText");
const activeCityStatEl = document.getElementById("activeCityStat");
const appStatePillEl = document.getElementById("appStatePill");
const selectAllBtn = document.getElementById("selectAllBtn");
const clearAllBtn = document.getElementById("clearAllBtn");
const locateMeBtn = document.getElementById("locateMeBtn");
const resetViewBtn = document.getElementById("resetViewBtn");
const mapSearchBtn = document.getElementById("mapSearchBtn");
const resetSettingsBtn = document.getElementById("resetSettingsBtn");
const mobilePanelToggle = document.getElementById("mobilePanelToggle");
const closePanelBtn = document.getElementById("closePanelBtn");
const sidebarEl = document.getElementById("sidebar");
const mobileSheetDismiss = document.getElementById("mobileSheetDismiss");
const routeModeToggle = document.getElementById("routeModeToggle");
const routeToggleLabel = document.querySelector(".route-toggle-inline");
const mapProviderSelect = document.getElementById("mapProviderSelect");
const travelModeSelect = document.getElementById("travelModeSelect");
const clearRouteBtn = document.getElementById("clearRouteBtn");
const routeFromEl = document.getElementById("routeFrom");
const routeToEl = document.getElementById("routeTo");
const hoursFilterList = document.getElementById("hoursFilterList");
const activeHoursText = document.getElementById("activeHoursText");
const textSearchInput = document.getElementById("textSearchInput");
const clearSearchBtn = document.getElementById("clearSearchBtn");
const searchShortcutList = document.getElementById("searchShortcutList");
const mapRouteToggleBtn = document.getElementById("mapRouteToggleBtn");
const mapHypotheticalToggleBtn = document.getElementById("mapHypotheticalToggleBtn");
const mapRouteLinkEl = document.getElementById("mapRouteLink");
const nearbyToggleBtn = document.getElementById("nearbyToggleBtn");
const nearbyStatusEl = document.getElementById("nearbyStatus");
const nearbyListEl = document.getElementById("nearbyList");
const clusterVisualizationToggleBtn = document.getElementById("clusterVisualizationToggleBtn");
const clusterDistanceSelect = document.getElementById("clusterDistanceSelect");
const clusterVisualizationStatusEl = document.getElementById("clusterVisualizationStatus");
const detailPanelEl = document.getElementById("detailPanel");
const detailPanelContentEl = document.getElementById("detailPanelContent");
const detailCloseBtn = document.getElementById("detailCloseBtn");

const fuzzySearcher = typeof window !== "undefined" && typeof window.uFuzzy === "function"
  ? new window.uFuzzy({ unicode: true })
  : null;

const map = L.map("map", {
  zoomControl: false,
  preferCanvas: true,
});

let zoomControl = null;
setZoomControlPosition();

const baseMapLayer = L.maplibreGL({
  style: MAP_STYLE_URL,
}).addTo(map);

state.markersLayer = L.layerGroup().addTo(map);
state.locationLayer = L.layerGroup().addTo(map);
state.clusterOverlayLayer = L.layerGroup().addTo(map);

disableServiceWorkersForStability();
updateConnectivityState();
window.addEventListener("online", updateConnectivityState);
window.addEventListener("offline", updateConnectivityState);
hydrateStateFromStoredPreferences();
window.addEventListener("pagehide", persistAppPreferences);
window.addEventListener("beforeunload", persistAppPreferences);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    persistAppPreferences();
  }
});

bootstrap().catch((error) => {
  console.error(error);
  setStatus("Failed to initialize app");
  setCitySelectMessage("Could not load cities", true);
});


async function bootstrap() {
  initCitySelect();
  if (textSearchInput) {
    textSearchInput.value = state.searchQuery;
  }
  initTravelModes();
  applyPendingTravelModePreference();
  renderHoursFilters();
  renderSearchShortcuts();
  renderClusterDistanceOptions();
  wireUi();
  updateRouteUi();
  updateHypotheticalUi();
  updateMobilePanelToggleUi();
  updateNearbyUi();
  updateWalkingClusterUi();
  updateActiveHoursSummary();
  setStatus("Loading your city guide…");
  await initDataBackend();

  if (!state.activeCity) {
    setStatus("No cities were found in the database.");
    return;
  }

  document.title = APP_NAME;

  const shouldPreserveStoredSelection = prepareStoredSelectionForActiveCity();
  await loadCategoriesForCity(state.activeCity, { preserveSelection: shouldPreserveStoredSelection });

  if (!restoreSavedMapView()) {
    resetMapToCity(state.activeCity);
  }

  await refreshMarkers();

  if (state.pendingNearbyModeEnabled) {
    await toggleNearbyMode(true, { silent: true });
  }

  await warmOfflineCache();

  const handleMapMoved = debounce(async () => {
    persistAppPreferences();
    await refreshMarkers();
  }, 120);

  map.on("moveend", handleMapMoved);
  map.on("click", handleMapClick);
  persistAppPreferences();
}


function getDefaultTravelModeForProvider(provider = detectDefaultRouteProvider()) {
  const allowedModes = TRAVEL_MODES_BY_PROVIDER[provider] || TRAVEL_MODES_BY_PROVIDER.google || [];
  if (allowedModes.includes("transit")) {
    return "transit";
  }
  return allowedModes[0] || "driving";
}

function hydrateStateFromStoredPreferences() {
  const stored = readStoredPreferences();
  if (!stored) {
    return;
  }

  state.scheduleFilter = stored.scheduleFilter;
  state.searchQuery = stored.searchQuery;
  state.routeProvider = stored.routeProvider;
  state.clusterVisualizationEnabled = stored.clusterVisualizationEnabled;
  state.clusterDistanceMeters = stored.clusterDistanceMeters;
  state.pendingActiveCity = stored.activeCity;
  state.pendingSelectedCategories = stored.selectedCategories;
  state.pendingTravelMode = stored.travelMode;
  state.pendingNearbyModeEnabled = stored.nearbyModeEnabled;
  state.pendingMapView = stored.mapView;
}

function readStoredPreferences() {
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return sanitizeStoredPreferences(JSON.parse(raw));
  } catch (error) {
    console.warn("Could not read saved settings", error);
    return null;
  }
}

function sanitizeStoredPreferences(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const routeProvider = typeof raw.routeProvider === "string" && ROUTE_PROVIDER_META[raw.routeProvider]
    ? raw.routeProvider
    : detectDefaultRouteProvider();
  const allowedTravelModes = TRAVEL_MODES_BY_PROVIDER[routeProvider] || TRAVEL_MODES_BY_PROVIDER.google || [];
  const scheduleFilter = typeof raw.scheduleFilter === "string" && HOURS_FILTERS[raw.scheduleFilter]
    ? raw.scheduleFilter
    : "all";
  const clusterDistanceMeters = WALKING_CLUSTER_DISTANCE_OPTIONS.includes(Number(raw.clusterDistanceMeters))
    ? Number(raw.clusterDistanceMeters)
    : DEFAULT_CLUSTER_DISTANCE_METERS;

  return {
    version: Number(raw.version) || SETTINGS_STORAGE_VERSION,
    activeCity: typeof raw.activeCity === "string" && raw.activeCity.trim() ? raw.activeCity : null,
    selectedCategories: Array.isArray(raw.selectedCategories)
      ? raw.selectedCategories.filter((value) => typeof value === "string")
      : null,
    searchQuery: typeof raw.searchQuery === "string" ? raw.searchQuery : "",
    scheduleFilter,
    routeProvider,
    travelMode: typeof raw.travelMode === "string" && allowedTravelModes.includes(raw.travelMode)
      ? raw.travelMode
      : getDefaultTravelModeForProvider(routeProvider),
    nearbyModeEnabled: Boolean(raw.nearbyModeEnabled),
    clusterVisualizationEnabled: Boolean(raw.clusterVisualizationEnabled),
    clusterDistanceMeters,
    mapView: sanitizeStoredMapView(raw.mapView),
  };
}

function sanitizeStoredMapView(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const centerLat = Number(raw.center?.lat);
  const centerLng = Number(raw.center?.lng);
  const zoom = Number(raw.zoom);
  const south = Number(raw.bounds?.south);
  const west = Number(raw.bounds?.west);
  const north = Number(raw.bounds?.north);
  const east = Number(raw.bounds?.east);

  const hasCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng);
  const hasZoom = Number.isFinite(zoom);
  const hasBounds = [south, west, north, east].every((value) => Number.isFinite(value));

  if (!hasCenter && !hasBounds) {
    return null;
  }

  return {
    center: hasCenter ? { lat: centerLat, lng: centerLng } : null,
    zoom: hasZoom ? zoom : null,
    bounds: hasBounds ? { south, west, north, east } : null,
  };
}

function getCurrentMapViewSnapshot() {
  if (!map || !map._loaded) {
    return null;
  }

  try {
    const center = map.getCenter();
    const bounds = map.getBounds();
    return {
      center: {
        lat: Number(center.lat.toFixed(6)),
        lng: Number(center.lng.toFixed(6)),
      },
      zoom: map.getZoom(),
      bounds: {
        south: Number(bounds.getSouth().toFixed(6)),
        west: Number(bounds.getWest().toFixed(6)),
        north: Number(bounds.getNorth().toFixed(6)),
        east: Number(bounds.getEast().toFixed(6)),
      },
    };
  } catch (error) {
    console.warn("Could not capture map view", error);
    return null;
  }
}

function buildStoredPreferences() {
  return {
    version: SETTINGS_STORAGE_VERSION,
    activeCity: state.activeCity || null,
    selectedCategories: Array.from(state.selectedCategories).sort(),
    searchQuery: state.searchQuery || "",
    scheduleFilter: HOURS_FILTERS[state.scheduleFilter] ? state.scheduleFilter : "all",
    routeProvider: ROUTE_PROVIDER_META[state.routeProvider] ? state.routeProvider : detectDefaultRouteProvider(),
    travelMode: travelModeSelect?.value || getDefaultTravelModeForProvider(state.routeProvider),
    nearbyModeEnabled: Boolean(state.nearbyModeEnabled),
    clusterVisualizationEnabled: Boolean(state.clusterVisualizationEnabled),
    clusterDistanceMeters: WALKING_CLUSTER_DISTANCE_OPTIONS.includes(Number(state.clusterDistanceMeters))
      ? Number(state.clusterDistanceMeters)
      : DEFAULT_CLUSTER_DISTANCE_METERS,
    mapView: getCurrentMapViewSnapshot(),
  };
}

function persistAppPreferences() {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(buildStoredPreferences()));
  } catch (error) {
    console.warn("Could not save settings", error);
  }
}

function prepareStoredSelectionForActiveCity() {
  const shouldPreserveSelection = Array.isArray(state.pendingSelectedCategories)
    && Boolean(state.activeCity)
    && state.pendingActiveCity === state.activeCity;

  if (shouldPreserveSelection) {
    state.selectedCategories = new Set(state.pendingSelectedCategories);
  }

  return shouldPreserveSelection;
}

function applyPendingTravelModePreference() {
  const pendingTravelMode = state.pendingTravelMode;
  if (!pendingTravelMode) {
    return;
  }

  const allowedModes = TRAVEL_MODES_BY_PROVIDER[state.routeProvider] || TRAVEL_MODES_BY_PROVIDER.google || [];
  travelModeSelect.value = allowedModes.includes(pendingTravelMode)
    ? pendingTravelMode
    : getDefaultTravelModeForProvider(state.routeProvider);
}

function restoreSavedMapView() {
  const savedMapView = state.pendingMapView;
  if (!savedMapView) {
    return false;
  }

  try {
    if (savedMapView.bounds) {
      map.fitBounds([
        [savedMapView.bounds.south, savedMapView.bounds.west],
        [savedMapView.bounds.north, savedMapView.bounds.east],
      ], {
        animate: false,
        padding: [0, 0],
      });
    }

    if (savedMapView.center && Number.isFinite(savedMapView.zoom)) {
      map.setView([savedMapView.center.lat, savedMapView.center.lng], savedMapView.zoom, {
        animate: false,
      });
    }

    return true;
  } catch (error) {
    console.warn("Could not restore saved map view", error);
    return false;
  }
}

async function resetSettingsToDefaults() {
  const preservedCity = state.activeCity || citySelect.value || Array.from(state.cityMetaByName.keys())[0] || null;

  if (state.nearbyModeEnabled) {
    await toggleNearbyMode(false, { silent: true });
  }

  clearRoutePoints();
  clearHypotheticalMarker();
  closeDetailPanel();

  state.routeModeEnabled = false;
  state.routeProvider = detectDefaultRouteProvider();
  state.clusterVisualizationEnabled = false;
  state.clusterDistanceMeters = DEFAULT_CLUSTER_DISTANCE_METERS;
  state.searchQuery = "";
  state.scheduleFilter = "all";
  state.pendingActiveCity = null;
  state.pendingSelectedCategories = null;
  state.pendingTravelMode = null;
  state.pendingNearbyModeEnabled = false;

  state.activeCity = preservedCity;
  citySelect.value = state.activeCity || "";

  if (textSearchInput) {
    textSearchInput.value = "";
  }

  mapProviderSelect.value = state.routeProvider;
  renderTravelModeOptions();
  travelModeSelect.value = getDefaultTravelModeForProvider(state.routeProvider);
  renderHoursFilters();
  renderSearchShortcuts();
  renderClusterDistanceOptions();
  updateActiveCityStat();
  updateActiveHoursSummary();
  updateRouteUi();
  updateNearbyUi();
  updateWalkingClusterUi();

  if (state.activeCity) {
    await loadCategoriesForCity(state.activeCity, { preserveSelection: false });
    await refreshMarkers();
  } else {
    state.selectedCategories.clear();
    state.visibleRows = [];
    state.markersLayer?.clearLayers();
    state.markerById.clear();
    renderWalkingClusterOverlays();
    markerCountEl.textContent = "0";
  }

  persistAppPreferences();
  setStatus("Settings reset. City and map view kept.");
}

function initCitySelect() {
  setCitySelectMessage("Loading cities…", true);
}

function shouldAttemptRemoteSqlite() {
  if (BUILD_STATIC_ONLY) {
    return { allowed: false, reason: "Static-only build: using bundled JSON dataset" };
  }

  if (runtimeConfig.staticOnly === true) {
    return { allowed: false, reason: "Static-only mode enabled by config" };
  }

  if (runtimeConfig.disableRemoteSqlite === true) {
    return { allowed: false, reason: "Remote SQLite disabled by config" };
  }

  if (typeof Worker === "undefined") {
    return { allowed: false, reason: "This browser does not support workers for remote SQLite" };
  }

  if (window.location.protocol === "http:" && !IS_LOOPBACK_HOST) {
    return {
      allowed: false,
      reason: "Compatibility mode: remote SQLite is skipped on non-localhost HTTP dev URLs",
    };
  }

  return { allowed: true, reason: "" };
}


async function initDataBackend() {
  const remoteSqliteEligibility = shouldAttemptRemoteSqlite();
  if (!remoteSqliteEligibility.allowed) {
    console.info(remoteSqliteEligibility.reason);
    await loadJsonFallback({ statusMessage: remoteSqliteEligibility.reason });
    return;
  }

  try {
    state.dbWorker = await initDatabaseWorker();
    state.backendMode = "sqlite-http";
    state.localRows = null;
    await loadCitiesFromDatabase();
  } catch (error) {
    console.warn("Falling back to bundled JSON dataset", error);
    await loadJsonFallback({ statusMessage: "Loaded in compatibility mode" });
  }
}

async function loadJsonFallback({ statusMessage = "Loaded in compatibility mode" } = {}) {
  const response = await fetch(`${DATA_JSON_URL}?v=${encodeURIComponent(DB_CACHE_BUST)}`, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Could not load fallback dataset (${response.status})`);
  }

  const rawText = await response.text();
  let parsedRows;
  try {
    parsedRows = JSON.parse(rawText);
  } catch (error) {
    const preview = rawText.slice(0, 48).replace(/\s+/g, " ");
    throw new Error(`Fallback dataset is not valid JSON: ${preview}`, { cause: error });
  }

  state.localRows = parsedRows;
  state.dbWorker = null;
  state.backendMode = "json";
  if (bytesReadEl) bytesReadEl.textContent = "Local";
  await loadCitiesFromDatabase();
  setStatus(statusMessage);
}

function setCitySelectMessage(message, disabled = true) {
  citySelect.innerHTML = "";
  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = message;
  citySelect.appendChild(placeholder);
  citySelect.disabled = disabled;
  citySelect.value = "";
}

async function loadCitiesFromDatabase() {
  let rows;

  if (state.localRows) {
    const statsByCity = new Map();
    for (const row of state.localRows) {
      const cityName = row.city_name;
      const lat = Number(row.lat);
      const lon = Number(row.lon);
      const current = statsByCity.get(cityName) || {
        city_name: cityName, item_count: 0, lat_sum: 0, lon_sum: 0,
        min_lat: lat, max_lat: lat, min_lon: lon, max_lon: lon,
      };
      current.item_count += 1;
      current.lat_sum += lat;
      current.lon_sum += lon;
      current.min_lat = Math.min(current.min_lat, lat);
      current.max_lat = Math.max(current.max_lat, lat);
      current.min_lon = Math.min(current.min_lon, lon);
      current.max_lon = Math.max(current.max_lon, lon);
      statsByCity.set(cityName, current);
    }
    rows = Array.from(statsByCity.values())
      .sort((a, b) => String(a.city_name).localeCompare(String(b.city_name)))
      .map((row) => ({
        city_name: row.city_name,
        item_count: row.item_count,
        avg_lat: row.lat_sum / row.item_count,
        avg_lon: row.lon_sum / row.item_count,
        min_lat: row.min_lat,
        max_lat: row.max_lat,
        min_lon: row.min_lon,
        max_lon: row.max_lon,
      }));
  } else {
    const sql = `
      SELECT
        city_name,
        COUNT(*) AS item_count,
        AVG(lat) AS avg_lat,
        AVG(lon) AS avg_lon,
        MIN(lat) AS min_lat,
        MAX(lat) AS max_lat,
        MIN(lon) AS min_lon,
        MAX(lon) AS max_lon
      FROM attraction
      GROUP BY city_name
      ORDER BY city_name
    `;
    rows = rowsFromExec(await state.dbWorker.db.exec(sql));
  }

  state.cityMetaByName = new Map(
    rows.map((row) => {
      const avgLat = Number(row.avg_lat);
      const avgLon = Number(row.avg_lon);
      const minLat = Number(row.min_lat);
      const maxLat = Number(row.max_lat);
      const minLon = Number(row.min_lon);
      const maxLon = Number(row.max_lon);
      return [
        row.city_name,
        {
          center: [avgLat, avgLon],
          zoom: DEFAULT_CITY_ZOOM,
          bounds: [
            [minLat, minLon],
            [maxLat, maxLon],
          ],
          itemCount: Number(row.item_count),
          timeZone: DEFAULT_CITY_TIME_ZONE,
        },
      ];
    }),
  );

  citySelect.innerHTML = "";

  const cityNames = Array.from(state.cityMetaByName.keys());
  for (const cityName of cityNames) {
    const option = document.createElement("option");
    option.value = cityName;
    option.textContent = cityName;
    citySelect.appendChild(option);
  }

  const preferredCity = state.pendingActiveCity;
  state.activeCity = preferredCity && cityNames.includes(preferredCity)
    ? preferredCity
    : cityNames[0] || null;
  citySelect.disabled = cityNames.length === 0;
  citySelect.value = state.activeCity || "";
  updateActiveCityStat();
  updateActiveHoursSummary();
}

function initTravelModes() {
  mapProviderSelect.innerHTML = "";
  for (const [value, meta] of Object.entries(ROUTE_PROVIDER_META)) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = meta.label;
    mapProviderSelect.appendChild(option);
  }
  mapProviderSelect.value = state.routeProvider;
  renderTravelModeOptions();
}

function renderTravelModeOptions() {
  const allowedModes = TRAVEL_MODES_BY_PROVIDER[state.routeProvider] || TRAVEL_MODES_BY_PROVIDER.google;
  const previousValue = travelModeSelect.value || state.pendingTravelMode || getDefaultTravelModeForProvider(state.routeProvider);
  travelModeSelect.innerHTML = "";

  for (const mode of allowedModes) {
    const option = document.createElement("option");
    option.value = mode;
    option.textContent = TRAVEL_MODE_LABELS[mode] || mode;
    travelModeSelect.appendChild(option);
  }

  if (allowedModes.includes(previousValue)) {
    travelModeSelect.value = previousValue;
  } else {
    travelModeSelect.value = getDefaultTravelModeForProvider(state.routeProvider);
  }
}

function renderHoursFilters() {
  hoursFilterList.innerHTML = "";
  for (const [value, meta] of Object.entries(HOURS_FILTERS)) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "filter-chip";
    button.dataset.mode = value;
    button.textContent = meta.label;
    button.setAttribute("aria-pressed", String(state.scheduleFilter === value));
    button.classList.toggle("is-active", state.scheduleFilter === value);
    button.addEventListener("click", async () => {
      state.scheduleFilter = value;
      renderHoursFilters();
      updateActiveHoursSummary();
      clearRoutePoints();
      persistAppPreferences();
      await refreshMarkers();
    });
    hoursFilterList.appendChild(button);
  }
}


function renderSearchShortcuts() {
  if (!searchShortcutList) {
    return;
  }

  const normalizedSearch = normalizeSearchQuery(state.searchQuery);
  searchShortcutList.innerHTML = "";

  for (const label of SEARCH_SHORTCUTS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "filter-chip search-shortcut-chip";
    button.textContent = label;
    const isActive = normalizedSearch === normalizeSearchQuery(label);
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", String(isActive));
    button.addEventListener("click", async () => {
      state.searchQuery = label;
      if (textSearchInput) {
        textSearchInput.value = label;
      }
      renderSearchShortcuts();
      clearRoutePoints();
      persistAppPreferences();
      await refreshMarkers();
    });
    searchShortcutList.appendChild(button);
  }
}

function renderClusterDistanceOptions() {
  if (!clusterDistanceSelect) {
    return;
  }

  clusterDistanceSelect.innerHTML = "";
  for (const meters of WALKING_CLUSTER_DISTANCE_OPTIONS) {
    const option = document.createElement("option");
    option.value = String(meters);
    option.textContent = `${meters} m`;
    clusterDistanceSelect.appendChild(option);
  }
  clusterDistanceSelect.value = String(state.clusterDistanceMeters);
}

function wireUi() {
  citySelect.addEventListener("change", async (event) => {
    state.activeCity = event.target.value;
    updateActiveCityStat();
    clearRoutePoints();
    closeDetailPanel();
    updateActiveHoursSummary();
    renderSearchShortcuts();
    setStatus(`Loading ${state.activeCity}…`);
    persistAppPreferences();
    await loadCategoriesForCity(state.activeCity, { preserveSelection: false });
    resetMapToCity(state.activeCity);
    await refreshMarkers();
    closeMobileSidebar();
  });

  selectAllBtn.addEventListener("click", async () => {
    state.selectedCategories = new Set(state.enabledCategories);
    syncCheckboxes();
    persistAppPreferences();
    await refreshMarkers();
  });

  clearAllBtn.addEventListener("click", async () => {
    state.selectedCategories.clear();
    syncCheckboxes();
    persistAppPreferences();
    await refreshMarkers();
  });

  const debouncedSearchRefresh = debounce(async () => {
    state.searchQuery = textSearchInput?.value || "";
    renderSearchShortcuts();
    clearRoutePoints();
    persistAppPreferences();
    await refreshMarkers();
  }, 180);

  textSearchInput?.addEventListener("input", () => {
    debouncedSearchRefresh();
  });

  textSearchInput?.addEventListener("search", () => {
    debouncedSearchRefresh();
  });

  clearSearchBtn?.addEventListener("click", async () => {
    state.searchQuery = "";
    if (textSearchInput) {
      textSearchInput.value = "";
      textSearchInput.focus();
    }
    renderSearchShortcuts();
    clearRoutePoints();
    persistAppPreferences();
    await refreshMarkers();
  });

  routeModeToggle.addEventListener("change", () => {
    setRouteMode(routeModeToggle.checked);
  });

  mapRouteToggleBtn.addEventListener("click", () => {
    setRouteMode(!state.routeModeEnabled, { viaMapToggle: true });
  });

  mapHypotheticalToggleBtn?.addEventListener("click", () => {
    setHypotheticalMode(!state.hypotheticalModeEnabled);
  });

  mapRouteLinkEl.addEventListener("click", (event) => {
    if (mapRouteLinkEl.classList.contains("is-disabled")) {
      event.preventDefault();
      setStatus(state.routeModeEnabled ? "Pick two markers to build a route." : "Turn routing on, then pick two markers.");
      return;
    }

    const providerLabel = ROUTE_PROVIDER_META[state.routeProvider]?.label || "your map app";
    setStatus(`Opening route in ${providerLabel}…`);
  });

  mapSearchBtn?.addEventListener("click", () => {
    openSelectedMarkerSearch();
  });

  mapProviderSelect.addEventListener("change", () => {
    state.routeProvider = mapProviderSelect.value || detectDefaultRouteProvider();
    renderTravelModeOptions();
    updateRouteUi();
    updateSelectedMarkerSearchUi();
    persistAppPreferences();
  });

  travelModeSelect.addEventListener("change", () => {
    updateRouteUi();
    persistAppPreferences();
  });

  clearRouteBtn.addEventListener("click", () => {
    clearRoutePoints();
    setStatus("Cleared route markers");
    persistAppPreferences();
  });

  nearbyToggleBtn.addEventListener("click", async () => {
    await toggleNearbyMode();
  });

  clusterVisualizationToggleBtn?.addEventListener("click", () => {
    state.clusterVisualizationEnabled = !state.clusterVisualizationEnabled;
    updateWalkingClusterUi();
    renderWalkingClusterOverlays();
    persistAppPreferences();
  });

  clusterDistanceSelect?.addEventListener("change", () => {
    const nextDistance = Number(clusterDistanceSelect.value);
    if (!Number.isFinite(nextDistance)) return;
    state.clusterDistanceMeters = nextDistance;
    updateWalkingClusterUi();
    renderWalkingClusterOverlays();
    persistAppPreferences();
  });

  resetSettingsBtn?.addEventListener("click", async () => {
    await resetSettingsToDefaults();
  });

  locateMeBtn.addEventListener("click", locateUser);
  resetViewBtn.addEventListener("click", async () => {
    resetMapToCity(state.activeCity);
    await refreshMarkers();
  });

  mobilePanelToggle.addEventListener("click", toggleMobileSidebar);
  closePanelBtn.addEventListener("click", closeMobileSidebar);
  mobileSheetDismiss?.addEventListener("click", closeMobileSidebar);
  detailCloseBtn?.addEventListener("click", closeDetailPanel);
  wireMobileSheetGestures();

  MOBILE_QUERY.addEventListener("change", (event) => {
    setZoomControlPosition(event.matches);
    if (!event.matches) {
      document.body.classList.remove("sidebar-open");
    }
    updateMobilePanelToggleUi();
  });
}

function setRouteMode(enabled, { viaMapToggle = false } = {}) {
  if (state.routeModeEnabled === enabled) {
    updateRouteUi();
    return;
  }

  state.routeModeEnabled = enabled;

  if (!enabled) {
    clearRoutePoints();
    setStatus(viaMapToggle ? "Routing turned off from the map." : "Routing is off.");
    return;
  }

  updateRouteUi();
  setStatus(viaMapToggle ? "Routing turned on. Tap two markers." : "Routing is on. Tap two markers.");
}

function setHypotheticalMode(enabled) {
  if (state.hypotheticalModeEnabled === enabled) {
    updateHypotheticalUi();
    return;
  }

  state.hypotheticalModeEnabled = enabled;

  if (!enabled) {
    updateHypotheticalUi();
    setStatus("Hypothetical marker mode is off.");
    return;
  }

  updateHypotheticalUi();
  setStatus("Hypothetical marker mode is on. Tap the map to place one marker.");
}

function updateHypotheticalUi() {
  mapHypotheticalToggleBtn?.classList.toggle("is-active", state.hypotheticalModeEnabled);
  mapHypotheticalToggleBtn?.setAttribute("aria-pressed", String(state.hypotheticalModeEnabled));
  if (mapHypotheticalToggleBtn) {
    mapHypotheticalToggleBtn.title = state.hypotheticalModeEnabled
      ? "Turn hypothetical marker mode off"
      : "Turn hypothetical marker mode on";
  }
}

function handleMapClick(event) {
  if (!state.hypotheticalModeEnabled) {
    return;
  }
  placeHypotheticalMarker(event.latlng);
}

function placeHypotheticalMarker(latlng) {
  const hadHypotheticalRoutePoint = state.routePoints.some((point) => point.id === "hypothetical");
  clearHypotheticalMarker({ keepPanel: true, keepRoutePoints: true });

  const summary = {
    id: "hypothetical",
    name: "Hypothetical stop",
    category_name: "Hypothetical",
    lat: latlng.lat,
    lon: latlng.lng,
  };

  const marker = L.circleMarker([latlng.lat, latlng.lng], {
    radius: 10,
    weight: 2,
    color: "#111827",
    fillColor: "#ffffff",
    fillOpacity: 0.92,
    dashArray: "5 4",
  }).addTo(map);

  marker.on("click", (event) => {
    L.DomEvent.stopPropagation(event);
    if (state.routeModeEnabled) {
      selectRoutePoint(summary);
    }
    openHypotheticalDetailPanel(summary);
  });

  state.hypotheticalMarker = marker;
  state.hypotheticalPoint = summary;

  if (hadHypotheticalRoutePoint) {
    state.routePoints = state.routePoints.map((point) => point.id === "hypothetical" ? summary : point);
    updateRouteUi();
  }

  openHypotheticalDetailPanel(summary);
  if (state.hypotheticalModeEnabled) {
    setHypotheticalMode(false);
    setStatus("Placed a hypothetical marker. Hypothetical mode is now off.");
  } else {
    setStatus("Placed a hypothetical marker.");
  }
}

function openHypotheticalDetailPanel(point) {
  if (!detailPanelEl || !detailPanelContentEl) {
    return;
  }

  state.activeDetailId = "hypothetical";
  state.detailRequestToken += 1;
  detailPanelEl.classList.remove("is-hidden");
  detailPanelContentEl.innerHTML = `
    <div class="detail-topline">
      <span class="detail-swatch detail-swatch--hypothetical"></span>
      <span class="detail-category">Hypothetical stop</span>
      <span class="detail-cluster">Planning aid</span>
    </div>
    <h2 class="detail-title">Hypothetical marker</h2>
    <div class="popup-muted detail-address">${escapeHtml(point.lat.toFixed(6))}, ${escapeHtml(point.lon.toFixed(6))}</div>
    <div class="popup-meta detail-meta">
      <span class="popup-chip">Temporary</span>
      <span class="popup-chip">Tap elsewhere to move it</span>
    </div>
    <p class="detail-description">Use this marker to sketch out a possible meeting point, stop, or route destination on the map.</p>
  `;
  updateRouteMarkerStyles();
  updateSelectedMarkerSearchUi();
}

function clearHypotheticalMarker({ keepPanel = false, keepRoutePoints = false } = {}) {
  if (state.hypotheticalMarker) {
    map.removeLayer(state.hypotheticalMarker);
  }
  state.hypotheticalMarker = null;
  state.hypotheticalPoint = null;
  if (!keepPanel && state.activeDetailId === "hypothetical") {
    closeDetailPanel();
  }
  if (!keepRoutePoints) {
    state.routePoints = state.routePoints.filter((point) => point.id !== "hypothetical");
    updateRouteUi();
  }
  updateHypotheticalUi();
}

function detectDefaultRouteProvider() {
  return "google";
}

function updateActiveCityStat() {
  if (!activeCityStatEl) return;
  activeCityStatEl.textContent = state.activeCity || "—";
}

async function disableServiceWorkersForStability() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    let didUnregister = false;

    for (const registration of registrations) {
      const scopeUrl = registration.scope || "";
      if (!scopeUrl.startsWith(window.location.origin)) continue;
      const ok = await registration.unregister();
      didUnregister = didUnregister || ok;
    }

    if (didUnregister) {
      state.offlineReady = false;
      state.serviceWorkerRegistration = null;

      if (navigator.serviceWorker.controller && !sessionStorage.getItem("roamdates_sw_disabled_once")) {
        sessionStorage.setItem("roamdates_sw_disabled_once", "1");
        window.location.reload();
        return;
      }
    }
  } catch (error) {
    console.warn("Unable to disable existing service workers", error);
  }
}

async function warmOfflineCache() {
  return;
}

function updateConnectivityState() {
  if (!appStatePillEl) return;

  if (!navigator.onLine) {
    appStatePillEl.dataset.state = "offline-limited";
    appStatePillEl.textContent = "Offline";
    return;
  }

  appStatePillEl.dataset.state = "online";
  appStatePillEl.textContent = "Online";
}

function setZoomControlPosition() {
  const targetPosition = MOBILE_QUERY.matches ? "topright" : "bottomright";
  if (zoomControl && zoomControl.options.position === targetPosition) {
    return;
  }
  if (zoomControl) {
    map.removeControl(zoomControl);
  }
  zoomControl = L.control.zoom({ position: targetPosition });
  zoomControl.addTo(map);
}

async function initDatabaseWorker() {
  const { createHttpBackend, createSQLiteThread } = await loadSqliteHttpModule();
  const httpBackend = createHttpBackend({
    maxPageSize: 4096,
    timeout: 10000,
    cacheSize: 4096,
  });

  const sqliteThread = await createSQLiteThread({ http: httpBackend });
  const remoteUrl = `${DB_SQLITE_URL}?v=${encodeURIComponent(DB_CACHE_BUST)}`;

  try {
    await sqliteThread("open", {
      filename: `file:${encodeURI(remoteUrl)}`,
      vfs: "http",
    });
  } catch (error) {
    sqliteThread.close?.();
    await httpBackend.close?.();
    throw error;
  }

  return {
    backend: httpBackend,
    sqliteThread,
    db: {
      async exec(sql, params = []) {
        const resultRows = [];
        await sqliteThread("exec", {
          sql,
          bind: params,
          rowMode: "object",
          callback(message) {
            if (message?.rowNumber === null || typeof message?.row === "undefined") {
              return;
            }
            resultRows.push(message.row);
          },
        });
        return resultRows;
      },
    },
    async close() {
      try {
        await sqliteThread("close", {});
      } catch (error) {
        console.warn("SQLite database close failed", error);
      }
      sqliteThread.close?.();
      await httpBackend.close?.();
    },
  };
}

async function loadCategoriesForCity(cityName, { preserveSelection }) {
  let rows;
  if (state.localRows) {
    const counts = new Map();
    for (const row of state.localRows) {
      if (row.city_name !== cityName) continue;
      counts.set(row.category_name, (counts.get(row.category_name) || 0) + 1);
    }
    rows = Array.from(counts.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
      .map(([category_name, item_count]) => ({ category_name, item_count }));
  } else {
    const sql = `
      SELECT category_name, COUNT(*) AS item_count
      FROM attraction
      WHERE city_name = ?
      GROUP BY category_name
      ORDER BY category_name
    `;
    rows = rowsFromExec(await state.dbWorker.db.exec(sql, [cityName]));
  }
  const countsByCategory = new Map(rows.map((row) => [row.category_name, Number(row.item_count)]));
  state.enabledCategories = new Set(
    CATEGORY_ORDER.filter((category) => (countsByCategory.get(category) || 0) > 0),
  );

  if (!preserveSelection) {
    state.selectedCategories = new Set(state.enabledCategories);
  } else {
    state.selectedCategories = new Set(
      Array.from(state.selectedCategories).filter((category) => state.enabledCategories.has(category)),
    );
  }

  renderCategoryList(countsByCategory);
}

function renderCategoryList(countsByCategory) {
  categoryList.innerHTML = "";

  for (const clusterName of CLUSTER_ORDER) {
    const clusterCategories = CATEGORY_ORDER.filter(
      (categoryName) => CATEGORY_META[categoryName]?.cluster === clusterName,
    );
    const enabledClusterCategories = clusterCategories.filter(
      (categoryName) => (countsByCategory.get(categoryName) || 0) > 0,
    );
    const selectedInCluster = enabledClusterCategories.filter(
      (categoryName) => state.selectedCategories.has(categoryName),
    );

    const section = document.createElement("section");
    section.className = "category-cluster";

    const header = document.createElement("div");
    header.className = "cluster-header";

    const title = document.createElement("div");
    title.className = "cluster-title";
    const dot = document.createElement("span");
    dot.className = "cluster-dot";
    dot.style.backgroundColor = CLUSTER_COLORS[clusterName] || "#6b7280";
    const titleText = document.createElement("span");
    titleText.textContent = clusterName;
    title.append(dot, titleText);

    const headerRight = document.createElement("div");
    headerRight.className = "cluster-header-right";

    const note = document.createElement("span");
    note.className = "cluster-note";
    note.textContent = `${selectedInCluster.length}/${enabledClusterCategories.length} selected`;

    const clusterToggleLabel = document.createElement("label");
    clusterToggleLabel.className = "cluster-toggle";
    const clusterToggle = document.createElement("input");
    clusterToggle.type = "checkbox";
    clusterToggle.checked = enabledClusterCategories.length > 0
      && selectedInCluster.length === enabledClusterCategories.length;
    clusterToggle.indeterminate = selectedInCluster.length > 0
      && selectedInCluster.length < enabledClusterCategories.length;
    clusterToggle.disabled = enabledClusterCategories.length === 0;
    clusterToggle.addEventListener("change", async () => {
      if (clusterToggle.checked) {
        for (const categoryName of enabledClusterCategories) {
          state.selectedCategories.add(categoryName);
        }
      } else {
        for (const categoryName of enabledClusterCategories) {
          state.selectedCategories.delete(categoryName);
        }
      }
      renderCategoryList(countsByCategory);
      persistAppPreferences();
      await refreshMarkers();
    });

    const toggleText = document.createElement("span");
    toggleText.textContent = "Cluster";
    clusterToggleLabel.append(clusterToggle, toggleText);

    headerRight.append(note, clusterToggleLabel);
    header.append(title, headerRight);

    const items = document.createElement("div");
    items.className = "cluster-items";

    for (const categoryName of clusterCategories) {
      const count = countsByCategory.get(categoryName) || 0;
      const isEnabled = count > 0;
      const meta = CATEGORY_META[categoryName] || { cluster: clusterName, color: "#6b7280" };

      const label = document.createElement("label");
      label.className = "category-item";

      const input = document.createElement("input");
      input.type = "checkbox";
      input.value = categoryName;
      input.checked = state.selectedCategories.has(categoryName);
      input.disabled = !isEnabled;
      input.addEventListener("change", async () => {
        if (input.checked) {
          state.selectedCategories.add(categoryName);
        } else {
          state.selectedCategories.delete(categoryName);
        }
        renderCategoryList(countsByCategory);
        persistAppPreferences();
        await refreshMarkers();
      });

      const swatch = document.createElement("span");
      swatch.className = "category-swatch";
      swatch.style.backgroundColor = meta.color;

      const text = document.createElement("span");
      text.className = "category-text";
      text.innerHTML = `<span class="category-name">${escapeHtml(categoryName)}</span><span class="category-cluster-name">${escapeHtml(meta.cluster)}</span>`;

      const countEl = document.createElement("span");
      countEl.className = "category-count";
      countEl.textContent = String(count);

      label.append(input, swatch, text, countEl);
      if (!isEnabled) {
        label.classList.add("is-disabled");
      }
      items.appendChild(label);
    }

    section.append(header, items);
    categoryList.appendChild(section);
  }
}

function syncCheckboxes() {
  for (const input of categoryList.querySelectorAll(".category-item input[type='checkbox']")) {
    input.checked = state.selectedCategories.has(input.value);
  }

  for (const section of categoryList.querySelectorAll(".category-cluster")) {
    const categoryInputs = Array.from(section.querySelectorAll(".category-item input[type='checkbox']"))
      .filter((input) => !input.disabled);
    const clusterToggle = section.querySelector(".cluster-toggle input[type='checkbox']");
    const clusterNote = section.querySelector(".cluster-note");
    const selectedCount = categoryInputs.filter((input) => input.checked).length;

    if (clusterToggle) {
      clusterToggle.checked = categoryInputs.length > 0 && selectedCount === categoryInputs.length;
      clusterToggle.indeterminate = selectedCount > 0 && selectedCount < categoryInputs.length;
      clusterToggle.disabled = categoryInputs.length === 0;
    }

    if (clusterNote) {
      clusterNote.textContent = `${selectedCount}/${categoryInputs.length} selected`;
    }
  }
}

async function refreshMarkers() {
  const requestToken = ++state.requestToken;
  state.currentCityClock = getCityTimeInfo(state.activeCity);
  updateActiveHoursSummary();

  if (state.selectedCategories.size === 0) {
    state.markersLayer.clearLayers();
    state.markerById.clear();
    state.visibleRows = [];
    markerCountEl.textContent = "0";
    renderWalkingClusterOverlays();
    updateRouteMarkerStyles();
    await refreshNearbyList();
    setStatus("Select at least one category");
    await updateBytesRead();
    return;
  }

  setStatus("Loading places…");

  const bounds = map.getBounds();
  const selectedCategories = Array.from(state.selectedCategories).sort();
  const allEnabledSelected = selectedCategories.length === state.enabledCategories.size
    && selectedCategories.every((category) => state.enabledCategories.has(category));

  const selectColumns = "id, city_name, name, description, address, lat, lon, category_name, price_label, closes_at";
  const whereParts = [
    "city_name = ?",
    "lat BETWEEN ? AND ?",
    "lon BETWEEN ? AND ?",
  ];
  const params = [
    state.activeCity,
    bounds.getSouth(),
    bounds.getNorth(),
    bounds.getWest(),
    bounds.getEast(),
  ];

  if (!allEnabledSelected) {
    const placeholders = selectedCategories.map(() => "?").join(", ");
    whereParts.push(`category_name IN (${placeholders})`);
    params.push(...selectedCategories);
  }

  const scheduleFilter = buildScheduleFilterClause(state.scheduleFilter, state.activeCity);
  if (scheduleFilter.clause) {
    whereParts.push(scheduleFilter.clause);
    params.push(...scheduleFilter.params);
  }


  let rows;

  if (state.localRows) {
    rows = applyTextSearchToRows(
      state.localRows.filter((row) => rowMatchesMapFilters(row, {
        cityName: state.activeCity,
        bounds,
        selectedCategories,
        allEnabledSelected,
        scheduleMode: state.scheduleFilter,
      })),
      state.searchQuery,
    );
  } else {
    const sql = `
      SELECT ${selectColumns}
      FROM attraction
      WHERE ${whereParts.join("\n      AND ")}
    `;

    rows = applyTextSearchToRows(rowsFromExec(await state.dbWorker.db.exec(sql, params)), state.searchQuery);
  }

  if (requestToken !== state.requestToken) {
    return;
  }

  state.visibleRows = rows.map((row) => ({
    ...row,
    id: Number(row.id),
    lat: Number(row.lat),
    lon: Number(row.lon),
    closes_at: row.closes_at == null ? null : Number(row.closes_at),
  }));

  state.markersLayer.clearLayers();
  state.markerById.clear();

  for (const row of rows) {
    const marker = buildMarker(row);
    state.markerById.set(Number(row.id), marker);
    marker.addTo(state.markersLayer);
  }

  if (state.activeDetailId && !state.markerById.has(state.activeDetailId)) {
    closeDetailPanel();
  }

  const visibleRoutePoints = state.routePoints.filter((point) => {
    if (point.id === "hypothetical") {
      return Boolean(state.hypotheticalMarker && state.hypotheticalPoint);
    }
    return state.markerById.has(point.id);
  });
  if (visibleRoutePoints.length !== state.routePoints.length) {
    state.routePoints = visibleRoutePoints;
  }

  updateRouteMarkerStyles();
  updateSelectedMarkerSearchUi();
  renderWalkingClusterOverlays();
  markerCountEl.textContent = String(rows.length);
  await refreshNearbyList();
  setStatus(buildResultsStatus(rows.length));
  await updateBytesRead();
  persistAppPreferences();
}

function updateWalkingClusterUi() {
  const isEnabled = Boolean(state.clusterVisualizationEnabled);
  clusterVisualizationToggleBtn?.classList.toggle("is-active", isEnabled);
  clusterVisualizationToggleBtn?.setAttribute("aria-pressed", String(isEnabled));
  if (clusterVisualizationToggleBtn) {
    clusterVisualizationToggleBtn.textContent = isEnabled ? "Zones On" : "Zones";
  }
  if (clusterDistanceSelect) {
    clusterDistanceSelect.value = String(state.clusterDistanceMeters);
  }

  if (!clusterVisualizationStatusEl) {
    return;
  }

  const clusterCount = computeWalkingClusters(state.visibleRows || [], state.clusterDistanceMeters).length;
  if (!isEnabled) {
    clusterVisualizationStatusEl.textContent = "Zones are off";
  } else if (clusterCount === 0) {
    clusterVisualizationStatusEl.textContent = `No groups at ${state.clusterDistanceMeters} m`;
  } else {
    clusterVisualizationStatusEl.textContent = `${clusterCount} group${clusterCount === 1 ? "" : "s"} at ${state.clusterDistanceMeters} m`;
  }
}

function renderWalkingClusterOverlays() {
  if (!state.clusterOverlayLayer) {
    return;
  }

  state.clusterOverlayLayer.clearLayers();

  if (!state.clusterVisualizationEnabled) {
    updateWalkingClusterUi();
    return;
  }

  const clusters = computeWalkingClusters(state.visibleRows || [], state.clusterDistanceMeters);
  for (const cluster of clusters) {
    const shape = L.polygon(cluster.hullLatLngs, {
      color: cluster.color,
      weight: 2,
      opacity: 0.7,
      fillColor: cluster.color,
      fillOpacity: 0.16,
      interactive: false,
      smoothFactor: 1,
      className: "walking-cluster-shape",
    });
    shape.addTo(state.clusterOverlayLayer);

    const label = L.marker(cluster.labelCenter || cluster.center, {
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: "walking-cluster-label",
        html: `<span>${cluster.count}</span>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      }),
    });
    label.addTo(state.clusterOverlayLayer);
  }

  updateWalkingClusterUi();
}

function computeWalkingClusters(rows, thresholdMeters) {
  const normalizedRows = Array.isArray(rows)
    ? rows
      .map((row) => ({
        ...row,
        lat: Number(row.lat),
        lon: Number(row.lon),
      }))
      .filter((row) => Number.isFinite(row.lat) && Number.isFinite(row.lon))
    : [];

  if (normalizedRows.length < 2 || !Number.isFinite(thresholdMeters) || thresholdMeters <= 0) {
    return [];
  }

  const connectRadius = Number(thresholdMeters);
  const connectRadiusSq = connectRadius * connectRadius;
  const cellSize = Math.max(1, connectRadius);
  const minNeighbors = getWalkingClusterMinNeighbors(connectRadius);
  const earthMetersPerDegree = (Math.PI * 6371000) / 180;
  const refLat = normalizedRows.reduce((sum, row) => sum + row.lat, 0) / normalizedRows.length;
  const refLon = normalizedRows.reduce((sum, row) => sum + row.lon, 0) / normalizedRows.length;
  const metersPerDegreeLat = earthMetersPerDegree;
  const metersPerDegreeLon = Math.max(earthMetersPerDegree * Math.cos(refLat * Math.PI / 180), 0.000001);
  const makeCellKey = (cellX, cellY) => `${cellX},${cellY}`;
  const projectX = (lon) => (lon - refLon) * metersPerDegreeLon;
  const projectY = (lat) => (lat - refLat) * metersPerDegreeLat;
  const toLatLng = (x, y) => [refLat + (y / metersPerDegreeLat), refLon + (x / metersPerDegreeLon)];
  const grid = new Map();

  const points = normalizedRows.map((row, index) => {
    const x = projectX(row.lon);
    const y = projectY(row.lat);
    const cellX = Math.floor(x / cellSize);
    const cellY = Math.floor(y / cellSize);
    const point = {
      index,
      row,
      x,
      y,
      cellX,
      cellY,
      neighbors: [],
      neighborCount: 0,
      isCore: false,
      clusterId: -1,
    };
    const cellKey = makeCellKey(cellX, cellY);
    if (!grid.has(cellKey)) {
      grid.set(cellKey, []);
    }
    grid.get(cellKey).push(index);
    return point;
  });

  function getCandidateIndexes(point) {
    const indexes = [];
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const bucket = grid.get(makeCellKey(point.cellX + dx, point.cellY + dy));
        if (bucket) {
          indexes.push(...bucket);
        }
      }
    }
    return indexes;
  }

  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    const candidateIndexes = getCandidateIndexes(point);

    for (const candidateIndex of candidateIndexes) {
      if (candidateIndex <= i) continue;
      const candidate = points[candidateIndex];
      const dx = point.x - candidate.x;
      const dy = point.y - candidate.y;
      if ((dx * dx) + (dy * dy) <= connectRadiusSq) {
        point.neighbors.push(candidateIndex);
        candidate.neighbors.push(i);
      }
    }
  }

  for (const point of points) {
    point.neighborCount = point.neighbors.length;
    point.isCore = point.neighborCount >= minNeighbors;
  }

  const coreVisited = new Array(points.length).fill(false);
  const rawClusters = [];

  for (let i = 0; i < points.length; i += 1) {
    const startPoint = points[i];
    if (coreVisited[i] || !startPoint.isCore) continue;

    const clusterId = rawClusters.length;
    const queue = [i];
    const coreIndexes = [];
    coreVisited[i] = true;

    while (queue.length) {
      const currentIndex = queue.shift();
      const currentPoint = points[currentIndex];
      currentPoint.clusterId = clusterId;
      coreIndexes.push(currentIndex);

      for (const neighborIndex of currentPoint.neighbors) {
        if (coreVisited[neighborIndex] || !points[neighborIndex].isCore) continue;
        coreVisited[neighborIndex] = true;
        queue.push(neighborIndex);
      }
    }

    if (coreIndexes.length > 0) {
      rawClusters.push({
        coreIndexes,
        memberIndexes: [...coreIndexes],
      });
    }
  }

  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    if (point.isCore) continue;

    let bestClusterId = -1;
    let bestDistanceSq = Number.POSITIVE_INFINITY;
    const candidateIndexes = getCandidateIndexes(point);

    for (const candidateIndex of candidateIndexes) {
      const candidate = points[candidateIndex];
      if (!candidate.isCore || candidate.clusterId < 0) continue;

      const dx = point.x - candidate.x;
      const dy = point.y - candidate.y;
      const distanceSq = (dx * dx) + (dy * dy);
      if (distanceSq <= connectRadiusSq && distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq;
        bestClusterId = candidate.clusterId;
      }
    }

    if (bestClusterId >= 0) {
      rawClusters[bestClusterId].memberIndexes.push(i);
    }
  }

  const clusters = [];

  for (const cluster of rawClusters) {
    const memberIndexes = [...new Set(cluster.memberIndexes)];
    if (memberIndexes.length < 2) continue;

    const memberPoints = memberIndexes.map((index) => points[index]);
    const memberRows = memberPoints.map((point) => point.row);
    const dominantCluster = getDominantClusterName(memberRows);
    const baseColor = CLUSTER_COLORS[dominantCluster] || "#5b6c87";

    let weightedX = 0;
    let weightedY = 0;
    let totalWeight = 0;
    for (const point of memberPoints) {
      const weight = Math.max(1, point.neighborCount);
      weightedX += point.x * weight;
      weightedY += point.y * weight;
      totalWeight += weight;
    }

    const weightedCenterX = totalWeight > 0
      ? weightedX / totalWeight
      : memberPoints.reduce((sum, point) => sum + point.x, 0) / memberPoints.length;
    const weightedCenterY = totalWeight > 0
      ? weightedY / totalWeight
      : memberPoints.reduce((sum, point) => sum + point.y, 0) / memberPoints.length;

    const hullRing = buildWalkingClusterHullRing(memberPoints, connectRadius);
    if (!hullRing || hullRing.length < 3) {
      continue;
    }

    const hullCenter = computePolygonCentroid(hullRing);
    const labelPoint = pickWalkingClusterLabelPoint(memberPoints, hullCenter.x, hullCenter.y) || { x: weightedCenterX, y: weightedCenterY };
    const hullLatLngs = hullRing.map((point) => toLatLng(point.x, point.y));
    const [centerLat, centerLon] = toLatLng(hullCenter.x, hullCenter.y);
    const [labelLat, labelLon] = toLatLng(labelPoint.x, labelPoint.y);

    clusters.push({
      count: memberPoints.length,
      center: [centerLat, centerLon],
      labelCenter: [labelLat, labelLon],
      hullLatLngs,
      hullArea: Math.abs(computePolygonSignedArea(hullRing)),
      color: baseColor,
      dominantCluster,
      distanceLabel: `${Math.round(thresholdMeters)}m`,
    });
  }

  return clusters.sort((a, b) => b.count - a.count || b.hullArea - a.hullArea);
}

function buildWalkingClusterHullRing(memberPoints, connectRadius) {
  const uniquePoints = dedupeClusterGeometryPoints(memberPoints);
  if (uniquePoints.length === 0) {
    return null;
  }

  let ring = null;
  if (uniquePoints.length >= 3) {
    ring = buildAlphaMaskClusterHull(uniquePoints, connectRadius);
  }
  if (!ring || ring.length < 3) {
    ring = buildFallbackClusterHull(uniquePoints, connectRadius);
  }
  if (!ring || ring.length < 3) {
    return null;
  }

  let cleaned = removeConsecutiveDuplicatePoints(ring);
  if (cleaned.length >= 3 && computePolygonSignedArea(cleaned) < 0) {
    cleaned = [...cleaned].reverse();
  }
  return cleaned;
}

function buildAlphaMaskClusterHull(points, connectRadius) {
  const cellSize = Math.max(22, Math.min(connectRadius / 6, 85));
  const influenceRadius = Math.max(cellSize * 1.75, Math.min(connectRadius * 0.43, connectRadius - 40));
  const influenceRadiusSq = influenceRadius * influenceRadius;
  const margin = influenceRadius + (cellSize * 2);
  const minX = Math.min(...points.map((point) => point.x)) - margin;
  const minY = Math.min(...points.map((point) => point.y)) - margin;
  const maxX = Math.max(...points.map((point) => point.x)) + margin;
  const maxY = Math.max(...points.map((point) => point.y)) + margin;
  const originX = Math.floor(minX / cellSize) * cellSize;
  const originY = Math.floor(minY / cellSize) * cellSize;
  const maxCellX = Math.ceil((maxX - originX) / cellSize);
  const maxCellY = Math.ceil((maxY - originY) / cellSize);
  const filledCells = new Set();

  for (const point of points) {
    const minCellXForPoint = Math.max(0, Math.floor((point.x - influenceRadius - originX) / cellSize));
    const maxCellXForPoint = Math.min(maxCellX, Math.ceil((point.x + influenceRadius - originX) / cellSize));
    const minCellYForPoint = Math.max(0, Math.floor((point.y - influenceRadius - originY) / cellSize));
    const maxCellYForPoint = Math.min(maxCellY, Math.ceil((point.y + influenceRadius - originY) / cellSize));

    for (let cellX = minCellXForPoint; cellX <= maxCellXForPoint; cellX += 1) {
      const centerX = originX + ((cellX + 0.5) * cellSize);
      for (let cellY = minCellYForPoint; cellY <= maxCellYForPoint; cellY += 1) {
        const centerY = originY + ((cellY + 0.5) * cellSize);
        const dx = centerX - point.x;
        const dy = centerY - point.y;
        if ((dx * dx) + (dy * dy) <= influenceRadiusSq) {
          filledCells.add(`${cellX},${cellY}`);
        }
      }
    }
  }

  if (filledCells.size === 0) {
    return null;
  }

  const loops = extractMaskLoopsFromFilledCells(filledCells, originX, originY, cellSize)
    .map((loop) => removeConsecutiveDuplicatePoints(loop))
    .filter((loop) => loop.length >= 4 && Math.abs(computePolygonSignedArea(loop)) >= (cellSize * cellSize * 3));

  if (loops.length === 0) {
    return null;
  }

  let ring = loops
    .sort((a, b) => Math.abs(computePolygonSignedArea(b)) - Math.abs(computePolygonSignedArea(a)))[0];

  ring = smoothClosedRing(ring, ring.length > 24 ? 2 : 1);
  ring = pruneShortHullSegments(ring, Math.max(10, cellSize * 0.3));
  if (ring.length < 3) {
    return null;
  }
  return ring;
}

function extractMaskLoopsFromFilledCells(filledCells, originX, originY, cellSize) {
  const outgoing = new Map();
  const incoming = new Map();
  const edges = [];

  function addEdge(startX, startY, endX, endY) {
    const startKey = `${startX},${startY}`;
    const endKey = `${endX},${endY}`;
    const edge = {
      id: edges.length,
      startKey,
      endKey,
      start: {
        x: originX + (startX * cellSize),
        y: originY + (startY * cellSize),
      },
      end: {
        x: originX + (endX * cellSize),
        y: originY + (endY * cellSize),
      },
      used: false,
    };
    edges.push(edge);
    if (!outgoing.has(startKey)) {
      outgoing.set(startKey, []);
    }
    outgoing.get(startKey).push(edge);
    if (!incoming.has(endKey)) {
      incoming.set(endKey, []);
    }
    incoming.get(endKey).push(edge);
  }

  for (const cellKey of filledCells) {
    const [cellXRaw, cellYRaw] = cellKey.split(",");
    const cellX = Number(cellXRaw);
    const cellY = Number(cellYRaw);
    if (!filledCells.has(`${cellX},${cellY - 1}`)) {
      addEdge(cellX, cellY, cellX + 1, cellY);
    }
    if (!filledCells.has(`${cellX + 1},${cellY}`)) {
      addEdge(cellX + 1, cellY, cellX + 1, cellY + 1);
    }
    if (!filledCells.has(`${cellX},${cellY + 1}`)) {
      addEdge(cellX + 1, cellY + 1, cellX, cellY + 1);
    }
    if (!filledCells.has(`${cellX - 1},${cellY}`)) {
      addEdge(cellX, cellY + 1, cellX, cellY);
    }
  }

  const loops = [];

  for (const edge of edges) {
    if (edge.used) continue;
    edge.used = true;
    const loop = [edge.start, edge.end];
    let currentKey = edge.endKey;
    let previousKey = edge.startKey;

    while (currentKey !== edge.startKey) {
      const nextEdges = (outgoing.get(currentKey) || []).filter((candidate) => !candidate.used);
      if (nextEdges.length === 0) {
        break;
      }
      let nextEdge = nextEdges[0];
      if (nextEdges.length > 1) {
        nextEdge = pickBestBoundaryEdge(nextEdges, previousKey, currentKey, incoming);
      }
      nextEdge.used = true;
      loop.push(nextEdge.end);
      previousKey = currentKey;
      currentKey = nextEdge.endKey;
    }

    if (loop.length >= 4) {
      loops.push(loop);
    }
  }

  return loops;
}

function pickBestBoundaryEdge(nextEdges, previousKey, currentKey) {
  const [prevX, prevY] = previousKey.split(",").map(Number);
  const [currX, currY] = currentKey.split(",").map(Number);
  const incomingDx = currX - prevX;
  const incomingDy = currY - prevY;

  const scored = nextEdges.map((edge) => {
    const [nextX, nextY] = edge.endKey.split(",").map(Number);
    const outgoingDx = nextX - currX;
    const outgoingDy = nextY - currY;
    const cross = (incomingDx * outgoingDy) - (incomingDy * outgoingDx);
    const dot = (incomingDx * outgoingDx) + (incomingDy * outgoingDy);
    const turnScore = Math.atan2(cross, dot);
    return { edge, turnScore };
  });

  scored.sort((a, b) => b.turnScore - a.turnScore);
  return scored[0].edge;
}

function smoothClosedRing(ring, iterations = 1) {
  let current = removeConsecutiveDuplicatePoints(ring);
  for (let iteration = 0; iteration < iterations; iteration += 1) {
    if (current.length < 3) {
      return current;
    }
    const next = [];
    for (let i = 0; i < current.length; i += 1) {
      const a = current[i];
      const b = current[(i + 1) % current.length];
      next.push({
        x: (0.75 * a.x) + (0.25 * b.x),
        y: (0.75 * a.y) + (0.25 * b.y),
      });
      next.push({
        x: (0.25 * a.x) + (0.75 * b.x),
        y: (0.25 * a.y) + (0.75 * b.y),
      });
    }
    current = next;
  }
  return current;
}

function pruneShortHullSegments(ring, minSegmentLength) {
  const points = removeConsecutiveDuplicatePoints(ring);
  if (points.length < 4) {
    return points;
  }
  const pruned = [];
  for (let i = 0; i < points.length; i += 1) {
    const prev = points[(i - 1 + points.length) % points.length];
    const point = points[i];
    const dx = point.x - prev.x;
    const dy = point.y - prev.y;
    if (pruned.length === 0 || Math.hypot(dx, dy) >= minSegmentLength) {
      pruned.push(point);
    }
  }
  return removeConsecutiveDuplicatePoints(pruned);
}

function buildFallbackClusterHull(points, connectRadius) {
  if (points.length === 1) {
    return buildPointBufferRing(points[0], Math.max(42, connectRadius * 0.2), 16);
  }
  if (points.length === 2) {
    return buildSegmentCapsuleRing(points[0], points[1], Math.max(42, Math.min(connectRadius * 0.26, 180)));
  }

  const hull = buildConvexHull(points);
  if (hull.length < 3) {
    return buildSegmentCapsuleRing(points[0], points[points.length - 1], Math.max(42, Math.min(connectRadius * 0.26, 180)));
  }

  const centroid = computePolygonCentroid(hull);
  const padding = Math.max(35, Math.min(connectRadius * 0.16, 140));
  return hull.map((point) => {
    const dx = point.x - centroid.x;
    const dy = point.y - centroid.y;
    const distance = Math.hypot(dx, dy) || 1;
    return {
      x: point.x + ((dx / distance) * padding),
      y: point.y + ((dy / distance) * padding),
    };
  });
}

function buildPointBufferRing(point, radius, segments = 16) {
  const ring = [];
  for (let i = 0; i < segments; i += 1) {
    const angle = (Math.PI * 2 * i) / segments;
    ring.push({
      x: point.x + (Math.cos(angle) * radius),
      y: point.y + (Math.sin(angle) * radius),
    });
  }
  return ring;
}

function buildSegmentCapsuleRing(a, b, radius) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const px = -uy;
  const py = ux;
  const steps = 8;
  const ring = [];

  for (let i = 0; i <= steps; i += 1) {
    const theta = (-Math.PI / 2) + ((Math.PI * i) / steps);
    ring.push({
      x: b.x + (ux * Math.cos(theta) * radius) + (px * Math.sin(theta) * radius),
      y: b.y + (uy * Math.cos(theta) * radius) + (py * Math.sin(theta) * radius),
    });
  }
  for (let i = 0; i <= steps; i += 1) {
    const theta = (Math.PI / 2) + ((Math.PI * i) / steps);
    ring.push({
      x: a.x + (ux * Math.cos(theta) * radius) + (px * Math.sin(theta) * radius),
      y: a.y + (uy * Math.cos(theta) * radius) + (py * Math.sin(theta) * radius),
    });
  }
  return ring;
}

function buildConvexHull(points) {
  if (points.length <= 1) {
    return [...points];
  }
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o, a, b) => ((a.x - o.x) * (b.y - o.y)) - ((a.y - o.y) * (b.x - o.x));
  const lower = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const point = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

function dedupeClusterGeometryPoints(points) {
  const seen = new Set();
  const deduped = [];
  for (const point of points) {
    const key = `${Math.round(point.x * 10)},${Math.round(point.y * 10)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push({
      x: point.x,
      y: point.y,
      neighborCount: point.neighborCount,
    });
  }
  return deduped;
}

function removeConsecutiveDuplicatePoints(points) {
  const deduped = [];
  for (const point of points) {
    const previous = deduped[deduped.length - 1];
    if (previous && Math.abs(previous.x - point.x) < 0.001 && Math.abs(previous.y - point.y) < 0.001) {
      continue;
    }
    deduped.push(point);
  }
  if (deduped.length > 2) {
    const first = deduped[0];
    const last = deduped[deduped.length - 1];
    if (Math.abs(first.x - last.x) < 0.001 && Math.abs(first.y - last.y) < 0.001) {
      deduped.pop();
    }
  }
  return deduped;
}

function computePolygonSignedArea(points) {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    area += (current.x * next.y) - (next.x * current.y);
  }
  return area / 2;
}

function computePolygonCentroid(points) {
  const signedArea = computePolygonSignedArea(points);
  if (!Number.isFinite(signedArea) || Math.abs(signedArea) < 1e-6) {
    return {
      x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
      y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
    };
  }
  let centroidX = 0;
  let centroidY = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    const cross = (current.x * next.y) - (next.x * current.y);
    centroidX += (current.x + next.x) * cross;
    centroidY += (current.y + next.y) * cross;
  }
  return {
    x: centroidX / (6 * signedArea),
    y: centroidY / (6 * signedArea),
  };
}

function pickWalkingClusterLabelPoint(points, targetX, targetY) {
  let bestPoint = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const point of points) {
    const dx = point.x - targetX;
    const dy = point.y - targetY;
    const distanceSq = (dx * dx) + (dy * dy);
    const densityBonus = (point.neighborCount || 0) * 225;
    const score = distanceSq - densityBonus;
    if (score < bestScore) {
      bestScore = score;
      bestPoint = point;
    }
  }
  return bestPoint;
}

function getWalkingClusterMinNeighbors(thresholdMeters) {
  if (thresholdMeters >= 1300) {
    return 3;
  }
  if (thresholdMeters >= 800) {
    return 2;
  }
  return 1;
}

function getDominantClusterName(rows) {
  const counts = new Map();
  for (const row of rows) {
    const clusterName = CATEGORY_META[row.category_name]?.cluster || "Practical";
    counts.set(clusterName, (counts.get(clusterName) || 0) + 1);
  }

  let winner = "Practical";
  let winnerCount = -1;
  for (const clusterName of CLUSTER_ORDER) {
    const count = counts.get(clusterName) || 0;
    if (count > winnerCount) {
      winner = clusterName;
      winnerCount = count;
    }
  }
  return winner;
}

function rowMatchesScheduleFilter(row, mode, cityName) {
  if (!row) return false;
  if (mode === "all") return true;

  const dayOffset = mode === "tomorrow" ? 1 : 0;
  const timeInfo = getCityTimeInfo(cityName, dayOffset);
  const weekdayBit = timeInfo.weekdayBit;
  const weekdaysMask = Number(row.weekdays_mask || 0);

  if ((weekdaysMask & weekdayBit) === 0) {
    return false;
  }

  if (mode === "today" || mode === "tomorrow") {
    return true;
  }

  const opensAt = Number(row.opens_at);
  const closesAt = Number(row.closes_at);
  const minutes = timeInfo.minutes;

  if (Number.isNaN(opensAt) || Number.isNaN(closesAt)) {
    return false;
  }

  if (closesAt >= opensAt) {
    return opensAt <= minutes && closesAt >= minutes;
  }

  return opensAt <= minutes || closesAt >= minutes;
}

function rowMatchesMapFilters(row, { cityName, bounds, selectedCategories, allEnabledSelected, scheduleMode }) {
  if (row.city_name !== cityName) return false;
  const lat = Number(row.lat);
  const lon = Number(row.lon);
  if (!bounds.contains([lat, lon])) return false;
  if (!allEnabledSelected && !selectedCategories.includes(row.category_name)) return false;
  return rowMatchesScheduleFilter(row, scheduleMode, cityName);
}

function rowMatchesNearbyFilters(row, { cityName, lat, lon, latDelta, lonDelta, selectedCategories, allEnabledSelected, scheduleMode }) {
  if (row.city_name !== cityName) return false;
  const rowLat = Number(row.lat);
  const rowLon = Number(row.lon);
  if (rowLat < lat - latDelta || rowLat > lat + latDelta) return false;
  if (rowLon < lon - lonDelta || rowLon > lon + lonDelta) return false;
  if (!allEnabledSelected && !selectedCategories.includes(row.category_name)) return false;
  return rowMatchesScheduleFilter(row, scheduleMode, cityName);
}

function normalizeSearchQuery(value) {
  return String(value || "").trim().toLowerCase();
}

function buildRowSearchText(row) {
  return [row.name, row.description, row.address, row.category_name]
    .filter(Boolean)
    .join(" ");
}

function applyTextSearchToRows(rows, searchQuery) {
  const normalizedQuery = normalizeSearchQuery(searchQuery);
  if (!normalizedQuery) {
    return rows;
  }

  if (fuzzySearcher) {
    const haystack = rows.map((row) => buildRowSearchText(row));
    const [idxs] = fuzzySearcher.search(haystack, normalizedQuery, UFUZZY_OUT_OF_ORDER_TERMS, UFUZZY_INFO_THRESHOLD);

    if (Array.isArray(idxs)) {
      return idxs.map((idx) => rows[idx]);
    }
  }

  return rows.filter((row) => buildRowSearchText(row).toLowerCase().includes(normalizedQuery));
}

function buildScheduleFilterClause(mode, cityName) {
  if (mode === "all") {
    return { clause: "", params: [] };
  }

  const dayOffset = mode === "tomorrow" ? 1 : 0;
  const timeInfo = getCityTimeInfo(cityName, dayOffset);
  const weekdayBit = timeInfo.weekdayBit;

  if (mode === "today" || mode === "tomorrow") {
    return {
      clause: "(weekdays_mask & ?) != 0",
      params: [weekdayBit],
    };
  }

  if (mode === "now") {
    return {
      clause: `
        (weekdays_mask & ?) != 0
        AND (
          (closes_at >= opens_at AND opens_at <= ? AND closes_at >= ?)
          OR
          (closes_at < opens_at AND (opens_at <= ? OR closes_at >= ?))
        )
      `,
      params: [weekdayBit, timeInfo.minutes, timeInfo.minutes, timeInfo.minutes, timeInfo.minutes],
    };
  }

  return { clause: "", params: [] };
}

function buildMarker(row) {
  const rowId = Number(row.id);
  const meta = CATEGORY_META[row.category_name] || { color: "#6b7280", cluster: "Other" };
  const marker = L.circleMarker([Number(row.lat), Number(row.lon)], baseMarkerStyle(row.category_name, meta.color));

  marker.rowData = {
    id: rowId,
    name: row.name,
    lat: Number(row.lat),
    lon: Number(row.lon),
    category_name: row.category_name,
    price_label: row.price_label,
    closes_at: row.closes_at == null ? null : Number(row.closes_at),
  };

  if (state.scheduleFilter === "today") {
    const closingHourLabel = getClosingHourLabel(marker.rowData.closes_at);
    if (closingHourLabel) {
      marker.bindTooltip(closingHourLabel, {
        permanent: true,
        direction: "top",
        offset: [0, -8],
        className: "closing-hour-tooltip",
      });
    }
  }

  marker.on("click", (event) => {
    openDetailPanel(marker.rowData);
    if (state.routeModeEnabled) {
      selectRoutePoint(marker.rowData);
    }
    if (event?.originalEvent) {
      event.originalEvent.preventDefault?.();
      event.originalEvent.stopPropagation?.();
    }
  });

  return marker;
}

function baseMarkerStyle(categoryName, color) {
  return {
    radius: categoryName === "Private Restrooms" ? 6 : 7,
    weight: 2,
    color: "#ffffff",
    fillColor: color,
    fillOpacity: categoryName === "Private Restrooms" ? 0.82 : 0.92,
  };
}

function updateRouteMarkerStyles() {
  for (const marker of state.markerById.values()) {
    const { rowData } = marker;
    const meta = CATEGORY_META[rowData.category_name] || { color: "#6b7280" };
    const baseStyle = baseMarkerStyle(rowData.category_name, meta.color);
    const isActiveDetail = state.activeDetailId === rowData.id;

    if (!state.routeModeEnabled) {
      marker.setStyle(isActiveDetail
        ? {
          ...baseStyle,
          radius: baseStyle.radius + 3,
          color: "#0f172a",
          weight: 3,
          fillOpacity: 1,
        }
        : baseStyle);
      continue;
    }

    const routeIndex = state.routePoints.findIndex((point) => point.id === rowData.id);

    if (routeIndex === 0) {
      marker.setStyle({
        ...baseStyle,
        radius: baseStyle.radius + 4,
        color: "#0f172a",
        weight: 4,
        fillOpacity: 1,
      });
    } else if (routeIndex === 1) {
      marker.setStyle({
        ...baseStyle,
        radius: baseStyle.radius + 4,
        color: "#7c2d12",
        weight: 4,
        fillOpacity: 1,
      });
    } else if (isActiveDetail) {
      marker.setStyle({
        ...baseStyle,
        radius: baseStyle.radius + 2,
        color: "#0f172a",
        weight: 3,
        fillOpacity: 1,
      });
    } else {
      marker.setStyle(baseStyle);
    }
  }

  if (state.hypotheticalMarker) {
    const baseStyle = {
      radius: 10,
      weight: 2,
      color: "#111827",
      fillColor: "#ffffff",
      fillOpacity: 0.92,
      dashArray: "5 4",
    };
    const routeIndex = state.routePoints.findIndex((point) => point.id === "hypothetical");
    const isActiveDetail = state.activeDetailId === "hypothetical";

    if (!state.routeModeEnabled) {
      state.hypotheticalMarker.setStyle(isActiveDetail
        ? {
          ...baseStyle,
          radius: baseStyle.radius + 2,
          weight: 3,
          color: "#0f172a",
          fillOpacity: 1,
        }
        : baseStyle);
      return;
    }

    if (routeIndex === 0) {
      state.hypotheticalMarker.setStyle({
        ...baseStyle,
        radius: baseStyle.radius + 3,
        color: "#0f172a",
        weight: 4,
        fillOpacity: 1,
      });
    } else if (routeIndex === 1) {
      state.hypotheticalMarker.setStyle({
        ...baseStyle,
        radius: baseStyle.radius + 3,
        color: "#7c2d12",
        weight: 4,
        fillOpacity: 1,
      });
    } else if (isActiveDetail) {
      state.hypotheticalMarker.setStyle({
        ...baseStyle,
        radius: baseStyle.radius + 2,
        color: "#0f172a",
        weight: 3,
        fillOpacity: 1,
      });
    } else {
      state.hypotheticalMarker.setStyle(baseStyle);
    }
  }
}

function selectRoutePoint(rowData) {
  const existingIndex = state.routePoints.findIndex((point) => point.id === rowData.id);

  if (existingIndex >= 0) {
    state.routePoints.splice(existingIndex, 1);
    updateRouteUi();
    setStatus("Removed route point");
    return;
  }

  const point = {
    id: rowData.id,
    name: rowData.name,
    lat: Number(rowData.lat),
    lon: Number(rowData.lon),
    category_name: rowData.category_name,
  };

  if (state.routePoints.length >= 2) {
    state.routePoints = [point];
    setStatus("Route reset. Pick a destination marker.");
  } else {
    state.routePoints = [...state.routePoints, point];
    setStatus(state.routePoints.length === 2 ? "Route ready" : "Pick one more marker");
  }

  updateRouteUi();
}

function clearRoutePoints() {
  state.routePoints = [];
  updateRouteUi();
}

function getSelectedMarkerSearchTarget() {
  if (state.activeDetailId == null || state.activeDetailId === "hypothetical") {
    return null;
  }

  const marker = state.markerById.get(Number(state.activeDetailId));
  if (marker?.rowData) {
    return marker.rowData;
  }

  const visibleRow = state.visibleRows.find((row) => Number(row.id) === Number(state.activeDetailId));
  return visibleRow || null;
}

function getSelectedMarkerSearchQuery(target, options = {}) {
  const { includeCity = false } = options;
  const rawName = typeof target?.name === "string" ? target.name.trim() : "";
  const cityName = typeof target?.city_name === "string" ? target.city_name.trim() : "";

  if (rawName) {
    if (includeCity && cityName) {
      const lowerName = rawName.toLocaleLowerCase();
      const lowerCity = cityName.toLocaleLowerCase();
      if (!lowerName.includes(lowerCity)) {
        return `${rawName}, ${cityName}`;
      }
    }
    return rawName;
  }

  const lat = Number(target?.lat);
  const lon = Number(target?.lon);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return `${lat},${lon}`;
  }
  return "";
}

function buildGoogleMapsSearchLink(target) {
  const url = new URL("https://www.google.com/maps/search/");
  url.searchParams.set("api", "1");
  url.searchParams.set("query", getSelectedMarkerSearchQuery(target, { includeCity: true }));
  return url.toString();
}

function buildAppleMapsSearchLink(target) {
  const url = new URL("https://maps.apple.com/");
  url.searchParams.set("q", getSelectedMarkerSearchQuery(target, { includeCity: true }));
  return url.toString();
}

function buildAmapSearchLink(target) {
  const url = new URL("https://m.amap.com/");
  url.searchParams.set("k", getSelectedMarkerSearchQuery(target, { includeCity: true }));
  url.searchParams.set("src", "roamdates");
  return url.toString();
}

function buildSelectedMarkerSearchLink(target, routeProvider) {
  if (!target) return "";
  if (routeProvider === "apple") {
    return buildAppleMapsSearchLink(target);
  }
  if (routeProvider === "amap" || routeProvider === "amap_mobile") {
    return buildAmapSearchLink(target);
  }
  return buildGoogleMapsSearchLink(target);
}

function updateSelectedMarkerSearchUi() {
  if (!mapSearchBtn) {
    return;
  }

  const target = getSelectedMarkerSearchTarget();
  const providerLabel = ROUTE_PROVIDER_META[state.routeProvider]?.label || "your selected map";

  mapSearchBtn.disabled = !target;
  mapSearchBtn.setAttribute("aria-disabled", String(!target));
  mapSearchBtn.title = target
    ? `Search ${target.name || "this place"} in ${providerLabel}`
    : `Select a marker to search it in ${providerLabel}`;
}

function openSelectedMarkerSearch() {
  const target = getSelectedMarkerSearchTarget();
  if (!target) {
    setStatus("Select a marker first, then tap S.");
    return;
  }

  const link = buildSelectedMarkerSearchLink(target, state.routeProvider || "google");
  const providerLabel = ROUTE_PROVIDER_META[state.routeProvider]?.label || "your selected map";

  if (!link) {
    setStatus(`Could not build a search link for ${providerLabel}.`);
    return;
  }

  window.open(link, "_blank", "noopener,noreferrer");
  setStatus(`Searching for ${target.name || "the selected place"} in ${providerLabel}…`);
}

function updateRouteUi() {
  const from = state.routePoints[0] || null;
  const to = state.routePoints[1] || null;
  const routeReady = Boolean(state.routeModeEnabled && from && to);
  const routeProvider = state.routeProvider || "google";
  const providerLabel = ROUTE_PROVIDER_META[routeProvider]?.label || "Map";

  routeModeToggle.checked = state.routeModeEnabled;
  routeToggleLabel?.classList.toggle("is-active", state.routeModeEnabled);
  mapProviderSelect.value = routeProvider;
  travelModeSelect.disabled = !state.routeModeEnabled;
  mapProviderSelect.disabled = false;
  mapRouteToggleBtn?.classList.toggle("is-active", state.routeModeEnabled);
  mapRouteToggleBtn?.setAttribute("aria-pressed", String(state.routeModeEnabled));
  mapRouteToggleBtn.title = state.routeModeEnabled ? "Turn routing off" : "Turn routing on";

  routeFromEl.textContent = from ? `${from.name} (${from.category_name})` : "Not selected";
  routeToEl.textContent = to ? `${to.name} (${to.category_name})` : "Not selected";

  const travelMode = travelModeSelect.value || "transit";
  mapRouteLinkEl.classList.toggle("is-routing", state.routeModeEnabled);

  if (routeReady) {
    const link = routeProvider === "apple"
      ? buildAppleMapsDirectionsLink(from, to, travelMode)
      : routeProvider === "amap_mobile"
        ? buildAmapMobileDirectionsLink(from, to, travelMode)
        : routeProvider === "amap"
          ? buildAmapDirectionsLink(from, to, travelMode)
          : buildGoogleMapsDirectionsLink(from, to, travelMode);
    mapRouteLinkEl.href = link;
    mapRouteLinkEl.classList.remove("is-disabled");
    mapRouteLinkEl.removeAttribute("aria-disabled");
    mapRouteLinkEl.title = `Open ${TRAVEL_MODE_LABELS[travelMode] || travelMode} route in ${providerLabel}`;
  } else {
    mapRouteLinkEl.href = "#";
    mapRouteLinkEl.classList.add("is-disabled");
    mapRouteLinkEl.setAttribute("aria-disabled", "true");
    mapRouteLinkEl.title = state.routeModeEnabled
      ? `Pick two markers to build a route in ${providerLabel}`
      : "Turn routing on, then pick two markers";
  }

  updateRouteMarkerStyles();
  updateSelectedMarkerSearchUi();
}

function buildGoogleMapsDirectionsLink(from, to, travelMode) {
  const url = new URL("https://www.google.com/maps/dir/");
  url.searchParams.set("api", "1");
  url.searchParams.set("origin", `${from.lat},${from.lon}`);
  url.searchParams.set("destination", `${to.lat},${to.lon}`);
  url.searchParams.set("travelmode", travelMode);
  return url.toString();
}

function buildAppleMapsDirectionsLink(from, to, travelMode) {
  const url = new URL("https://maps.apple.com/");
  url.searchParams.set("saddr", `${from.lat},${from.lon}`);
  url.searchParams.set("daddr", `${to.lat},${to.lon}`);
  const dirFlags = {
    driving: "d",
    walking: "w",
    transit: "r",
  };
  if (dirFlags[travelMode]) {
    url.searchParams.set("dirflg", dirFlags[travelMode]);
  }
  return url.toString();
}

function buildAmapDirectionsBaseLink(from, to, travelMode, callnative = "0") {
  const url = new URL("https://uri.amap.com/navigation");
  const amapMode = {
    driving: "car",
    walking: "walk",
    transit: "bus",
  };
  url.searchParams.set("from", `${from.lon},${from.lat},${from.name}`);
  url.searchParams.set("to", `${to.lon},${to.lat},${to.name}`);
  url.searchParams.set("mode", amapMode[travelMode] || "bus");
  url.searchParams.set("policy", "0");
  url.searchParams.set("src", "roamdates");
  url.searchParams.set("coordinate", "wgs84");
  url.searchParams.set("callnative", callnative);
  return url.toString();
}

function buildAmapDirectionsLink(from, to, travelMode) {
  return buildAmapDirectionsBaseLink(from, to, travelMode, "0");
}

function getAmapMobilePlatform() {
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return null;
}

function buildAmapMobileAppSchemeLink(from, to, travelMode) {
  const platform = getAmapMobilePlatform();
  const appMode = {
    driving: "0",
    transit: "1",
    walking: "2",
  };
  const params = new URLSearchParams();
  params.set("sourceApplication", "roamdates");
  params.set("slat", String(from.lat));
  params.set("slon", String(from.lon));
  params.set("sname", from.name || "Start");
  params.set("dlat", String(to.lat));
  params.set("dlon", String(to.lon));
  params.set("dname", to.name || "Destination");
  params.set("dev", "1");
  params.set("t", appMode[travelMode] || "1");

  if (platform === "ios") {
    return `iosamap://path?${params.toString()}`;
  }
  return `amapuri://route/plan/?${params.toString()}`;
}

function buildAmapMobileDirectionsLink(from, to, travelMode) {
  const platform = getAmapMobilePlatform();
  if (platform === "ios" || platform === "android") {
    return buildAmapMobileAppSchemeLink(from, to, travelMode);
  }
  return buildAmapDirectionsBaseLink(from, to, travelMode, "1");
}

async function fetchAttractionById(id) {
  if (state.localRows) {
    const row = state.localRows.find((item) => Number(item.id) === Number(id));
    if (!row) return null;
    return {
      description: row.description,
      address: row.address,
      price_label: row.price_label,
      weekdays_mask: row.weekdays_mask,
      opens_at: row.opens_at,
      closes_at: row.closes_at,
    };
  }
  const sql = `
    SELECT description, address, price_label, weekdays_mask, opens_at, closes_at
    FROM attraction
    WHERE id = ?
  `;
  const rows = rowsFromExec(await state.dbWorker.db.exec(sql, [id]));
  return rows[0] || null;
}

function renderDetailHtml(summary, details) {
  if (!details) {
    return `<div class="detail-empty"><strong>${escapeHtml(summary.name)}</strong><br>Details unavailable.</div>`;
  }

  const meta = CATEGORY_META[summary.category_name] || { cluster: "Other", color: "#6b7280" };
  const distanceHtml = state.userLocation
    ? `<span class="popup-chip">${formatDistanceMeters(distanceBetween(state.userLocation.lat, state.userLocation.lon, Number(summary.lat), Number(summary.lon)))}</span>`
    : "";
  const routeHint = state.routeModeEnabled
    ? `<div class="popup-muted popup-route-hint">Route mode is on. Tap markers to set your start and destination.</div>`
    : "";

  return `
    <div class="detail-topline">
      <span class="detail-swatch" style="background:${escapeHtml(meta.color)}"></span>
      <span class="detail-category">${escapeHtml(summary.category_name)}</span>
      <span class="detail-cluster">${escapeHtml(meta.cluster)}</span>
    </div>
    <h2 class="detail-title">${escapeHtml(summary.name)}</h2>
    <div class="popup-muted detail-address">${escapeHtml(details.address || "Address unavailable")}</div>
    <div class="popup-meta detail-meta">
      <span class="popup-chip">${escapeHtml(details.price_label || "free")}</span>
      <span class="popup-chip">${escapeHtml(formatHours(details.weekdays_mask, details.opens_at, details.closes_at))}</span>
      ${distanceHtml}
    </div>
    ${routeHint}
    <p class="detail-description">${escapeHtml(details.description || "")}</p>
  `;
}

async function openDetailPanel(summary) {
  if (!detailPanelEl || !detailPanelContentEl) {
    return;
  }

  state.activeDetailId = Number(summary.id);
  state.detailRequestToken += 1;
  const requestToken = state.detailRequestToken;
  detailPanelEl.classList.remove("is-hidden");
  detailPanelContentEl.innerHTML = `
    <div class="detail-topline">
      <span class="detail-swatch" style="background:${escapeHtml((CATEGORY_META[summary.category_name] || { color: "#6b7280" }).color)}"></span>
      <span class="detail-category">${escapeHtml(summary.category_name)}</span>
    </div>
    <h2 class="detail-title">${escapeHtml(summary.name)}</h2>
    <div class="detail-empty">Loading details…</div>
  `;
  updateRouteMarkerStyles();
  updateSelectedMarkerSearchUi();

  const details = await fetchAttractionById(state.activeDetailId);
  if (requestToken !== state.detailRequestToken || Number(summary.id) !== state.activeDetailId) {
    return;
  }

  detailPanelContentEl.innerHTML = renderDetailHtml(summary, details);
  await updateBytesRead();
}

function closeDetailPanel() {
  state.activeDetailId = null;
  state.detailRequestToken += 1;
  detailPanelEl?.classList.add("is-hidden");
  if (detailPanelContentEl) {
    detailPanelContentEl.innerHTML = '<div class="detail-empty">Tap a marker to see place details here.</div>';
  }
  updateRouteMarkerStyles();
  updateSelectedMarkerSearchUi();
}

async function locateUser() {
  if (!navigator.geolocation) {
    setStatus("Geolocation is not supported in this browser");
    return;
  }

  if (!window.isSecureContext) {
    setStatus("Location needs HTTPS or localhost to work");
    return;
  }

  setStatus("Finding your location…");

  try {
    const position = await getBestCurrentPosition();
    applyPosition(position, { centerMap: true });
    if (state.nearbyModeEnabled) {
      await refreshNearbyList();
    }
    setStatus("Centered on your location");
  } catch (error) {
    console.error(error);
    setStatus(describeLocationError(error));
  }
}

async function getBestCurrentPosition() {
  const isiOSLike = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const attempts = isiOSLike
    ? [
      () => getCurrentPositionByWatch({ enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }),
      () => getCurrentPositionAsync({ enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }),
      () => getCurrentPositionAsync({ enableHighAccuracy: false, timeout: 12000, maximumAge: 30000 }),
    ]
    : [
      () => getCurrentPositionAsync({ enableHighAccuracy: false, timeout: 9000, maximumAge: 30000 }),
      () => getCurrentPositionByWatch({ enableHighAccuracy: true, timeout: 18000, maximumAge: 0 }),
    ];

  let lastError = null;
  for (const attempt of attempts) {
    try {
      return await attempt();
    } catch (error) {
      lastError = error;
      console.warn("Location attempt failed", error);
    }
  }

  throw lastError || new Error("Could not access your location");
}

function getCurrentPositionAsync(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

function getCurrentPositionByWatch(options) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutMs = Math.max((options?.timeout || 15000) + 2000, 6000);
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      if (watchId != null) {
        navigator.geolocation.clearWatch(watchId);
      }
      window.clearTimeout(timerId);
      callback(value);
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => finish(resolve, position),
      (error) => finish(reject, error),
      options,
    );

    const timerId = window.setTimeout(() => {
      const timeoutError = new Error("Location request timed out");
      timeoutError.code = 3;
      finish(reject, timeoutError);
    }, timeoutMs);
  });
}

function describeLocationError(error) {
  if (error?.code === 1) {
    return "Location permission is blocked. Allow location access in Safari and try again.";
  }
  if (error?.code === 2) {
    return "Your location is unavailable right now. Try again outdoors or after a moment.";
  }
  if (error?.code === 3) {
    return "Location took too long. Try again or check that precise location is enabled.";
  }
  return "Could not access your location";
}

function applyPosition(position, { centerMap = false } = {}) {
  const lat = position.coords.latitude;
  const lon = position.coords.longitude;
  const accuracy = Math.max(position.coords.accuracy || 0, 25);
  state.userLocation = { lat, lon, accuracy };
  renderUserLocation();

  if (centerMap) {
    map.flyTo([lat, lon], Math.max(map.getZoom(), 14), { duration: 0.8 });
  }
}

async function toggleNearbyMode(forceValue = null, { silent = false } = {}) {
  const nextValue = forceValue == null ? !state.nearbyModeEnabled : Boolean(forceValue);

  if (nextValue === state.nearbyModeEnabled) {
    updateNearbyUi();
    updateWalkingClusterUi();
    persistAppPreferences();
    return;
  }

  state.nearbyModeEnabled = nextValue;

  if (!nextValue) {
    stopLocationWatch();
    updateNearbyUi();
    updateWalkingClusterUi();
    persistAppPreferences();
    if (!silent) {
      setStatus("Nearby tracking turned off.");
    }
    return;
  }

  if (!navigator.geolocation) {
    state.nearbyModeEnabled = false;
    updateNearbyUi();
    updateWalkingClusterUi();
    persistAppPreferences();
    if (!silent) {
      setStatus("Geolocation is not supported in this browser");
    }
    return;
  }

  startLocationWatch();
  updateNearbyUi();
  updateWalkingClusterUi();
  persistAppPreferences();
  if (!silent) {
    setStatus("Nearby tracking turned on. Waiting for your location…");
  }

  if (state.userLocation) {
    await refreshNearbyList();
  }
}

function startLocationWatch() {
  if (state.locationWatchId !== null || !navigator.geolocation) {
    return;
  }

  state.locationWatchId = navigator.geolocation.watchPosition(
    async (position) => {
      const hadLocation = Boolean(state.userLocation);
      applyPosition(position, { centerMap: !hadLocation });
      if (state.nearbyModeEnabled) {
        await refreshNearbyList();
      }
    },
    (error) => {
      console.error(error);
      stopLocationWatch();
      state.nearbyModeEnabled = false;
      updateNearbyUi();
      updateWalkingClusterUi();
      persistAppPreferences();
      setStatus("Could not keep tracking your location");
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    },
  );
}

function stopLocationWatch() {
  if (state.locationWatchId !== null && navigator.geolocation) {
    navigator.geolocation.clearWatch(state.locationWatchId);
    state.locationWatchId = null;
  }
}

async function refreshNearbyList() {
  if (!state.nearbyModeEnabled) {
    updateNearbyUi();
    updateWalkingClusterUi();
    return;
  }

  if (!state.userLocation) {
    updateNearbyUi();
    updateWalkingClusterUi();
    return;
  }

  if (!state.activeCity) {
    nearbyStatusEl.textContent = "Loading city data";
    return;
  }

  if (!state.localRows && !state.dbWorker?.db) {
    nearbyStatusEl.textContent = "Database is still loading";
    return;
  }

  if (state.selectedCategories.size === 0) {
    renderNearbyList([]);
    nearbyStatusEl.textContent = "Turn on categories to see nearby places";
    return;
  }

  const { lat, lon } = state.userLocation;
  const latDelta = WALKING_DISTANCE_METERS / 111320;
  const lonScale = Math.max(Math.cos((lat * Math.PI) / 180), 0.2);
  const lonDelta = WALKING_DISTANCE_METERS / (111320 * lonScale);

  const selectedCategories = Array.from(state.selectedCategories).sort();
  const allEnabledSelected =
    selectedCategories.length === state.enabledCategories.size &&
    selectedCategories.every((category) => state.enabledCategories.has(category));

  const whereParts = [
    "city_name = ?",
    "lat BETWEEN ? AND ?",
    "lon BETWEEN ? AND ?",
  ];

  const params = [
    state.activeCity,
    lat - latDelta,
    lat + latDelta,
    lon - lonDelta,
    lon + lonDelta,
  ];

  if (!allEnabledSelected) {
    const placeholders = selectedCategories.map(() => "?").join(", ");
    whereParts.push(`category_name IN (${placeholders})`);
    params.push(...selectedCategories);
  }

  const scheduleFilter = buildScheduleFilterClause(state.scheduleFilter, state.activeCity);
  if (scheduleFilter.clause) {
    whereParts.push(`(${scheduleFilter.clause})`);
    params.push(...scheduleFilter.params);
  }

  let rows;

  if (state.localRows) {
    rows = applyTextSearchToRows(
      state.localRows
        .filter((row) => rowMatchesNearbyFilters(row, {
          cityName: state.activeCity,
          lat,
          lon,
          latDelta,
          lonDelta,
          selectedCategories,
          allEnabledSelected,
          scheduleMode: state.scheduleFilter,
        }))
        .map((row) => ({
          ...row,
          distance_meters: distanceBetween(lat, lon, Number(row.lat), Number(row.lon)),
        }))
        .filter((row) => row.distance_meters <= WALKING_DISTANCE_METERS)
        .sort((a, b) => a.distance_meters - b.distance_meters || String(a.name).localeCompare(String(b.name))),
      state.searchQuery,
    );
  } else {
    const sql = [
      "SELECT id, name, description, address, lat, lon, category_name, price_label, closes_at",
      "FROM attraction",
      `WHERE ${whereParts.join(" AND ")}`,
    ].join("\n");

    const result = await state.dbWorker.db.exec(sql, params);
    rows = applyTextSearchToRows(
      rowsFromExec(result)
        .map((row) => ({
          ...row,
          distance_meters: distanceBetween(lat, lon, Number(row.lat), Number(row.lon)),
        }))
        .filter((row) => row.distance_meters <= WALKING_DISTANCE_METERS)
        .sort((a, b) => a.distance_meters - b.distance_meters || String(a.name).localeCompare(String(b.name))),
      state.searchQuery,
    );
  }

  renderNearbyList(rows);
  nearbyStatusEl.textContent = `${rows.length} place${rows.length === 1 ? "" : "s"} within about ${Math.round((WALKING_DISTANCE_METERS / 1000) * 10) / 10} km`;
  await updateBytesRead();
}

function updateNearbyUi() {
  nearbyToggleBtn.classList.toggle("is-active", state.nearbyModeEnabled);
  nearbyToggleBtn.setAttribute("aria-pressed", String(state.nearbyModeEnabled));
  nearbyToggleBtn.textContent = state.nearbyModeEnabled ? "Nearby On" : "Nearby";

  if (!state.nearbyModeEnabled) {
    nearbyStatusEl.textContent = "Nearby is off";
    nearbyListEl.className = "nearby-list nearby-empty";
    nearbyListEl.textContent = "Turn on Nearby to build a walking-distance list.";
  } else if (!state.userLocation) {
    nearbyStatusEl.textContent = "Waiting for your location";
    nearbyListEl.className = "nearby-list nearby-empty";
    nearbyListEl.textContent = "Allow location access to build the nearby list.";
  }
}

function renderNearbyList(rows) {
  nearbyListEl.innerHTML = "";

  if (!rows.length) {
    nearbyListEl.className = "nearby-list nearby-empty";
    nearbyListEl.textContent = state.userLocation
      ? `No selected places are within about ${Math.round(WALKING_DISTANCE_METERS / 1000 * 10) / 10} km right now.`
      : "Allow location access to build the nearby list.";
    return;
  }

  nearbyListEl.className = "nearby-list";

  for (const row of rows) {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "nearby-item";

    const meta = CATEGORY_META[row.category_name] || { color: "#6b7280", cluster: "Other" };

    item.innerHTML = `
      <span class="nearby-swatch" style="background:${escapeHtml(meta.color)}"></span>
      <span class="nearby-copy">
        <span class="nearby-name">${escapeHtml(row.name)}</span>
        <span class="nearby-meta">${escapeHtml(row.category_name)} · ${escapeHtml(meta.cluster)} · ${escapeHtml(formatDistanceMeters(row.distance_meters))}</span>
      </span>
      <span class="nearby-chevron" aria-hidden="true">›</span>
    `;

    item.addEventListener("click", () => {
      const marker = state.markerById.get(Number(row.id));
      if (marker) {
        map.flyTo([Number(row.lat), Number(row.lon)], Math.max(map.getZoom(), 15), { duration: 0.45 });
        window.setTimeout(() => openDetailPanel(marker.rowData), 220);
      } else {
        map.flyTo([Number(row.lat), Number(row.lon)], Math.max(map.getZoom(), 15), { duration: 0.45 });
        openDetailPanel({
          id: Number(row.id),
          name: row.name,
          lat: Number(row.lat),
          lon: Number(row.lon),
          category_name: row.category_name,
          price_label: row.price_label,
          closes_at: row.closes_at == null ? null : Number(row.closes_at),
        });
      }
      if (MOBILE_QUERY.matches) {
        closeMobileSidebar();
      }
    });

    nearbyListEl.appendChild(item);
  }
}

function renderUserLocation() {
  state.locationLayer.clearLayers();
  if (!state.userLocation) return;

  const { lat, lon, accuracy } = state.userLocation;
  L.circle([lat, lon], {
    radius: accuracy,
    stroke: false,
    fillColor: "#2f6bff",
    fillOpacity: 0.12,
  }).addTo(state.locationLayer);

  L.circleMarker([lat, lon], {
    radius: 8,
    weight: 3,
    color: "#ffffff",
    fillColor: "#2f6bff",
    fillOpacity: 1,
  }).bindPopup("You are here").addTo(state.locationLayer);
}

function resetMapToCity(cityName) {
  const cityMeta = state.cityMetaByName.get(cityName);
  if (!cityMeta) {
    map.setView([35.6804, 139.769], DEFAULT_CITY_ZOOM);
    return;
  }

  const [[minLat, minLon], [maxLat, maxLon]] = cityMeta.bounds || [];
  if (
    Number.isFinite(minLat) && Number.isFinite(minLon)
    && Number.isFinite(maxLat) && Number.isFinite(maxLon)
    && (minLat !== maxLat || minLon !== maxLon)
  ) {
    map.fitBounds([[minLat, minLon], [maxLat, maxLon]], {
      padding: [28, 28],
      maxZoom: 13,
    });
    return;
  }

  map.setView(cityMeta.center || [35.6804, 139.769], cityMeta.zoom || DEFAULT_CITY_ZOOM);
}

function wireMobileSheetGestures() {
  if (!sidebarEl) return;

  let startY = null;
  let startX = null;
  let tracking = false;

  const canTrack = (event) => {
    if (!MOBILE_QUERY.matches || !document.body.classList.contains("sidebar-open")) {
      return false;
    }
    const touch = event.touches?.[0];
    if (!touch) return false;
    if (mobileSheetDismiss?.contains(event.target)) {
      return true;
    }
    const rect = sidebarEl.getBoundingClientRect();
    return touch.clientY >= rect.bottom - 84;
  };

  const onTouchStart = (event) => {
    if (!canTrack(event)) {
      tracking = false;
      return;
    }
    const touch = event.touches[0];
    startY = touch.clientY;
    startX = touch.clientX;
    tracking = true;
  };

  const onTouchMove = (event) => {
    if (!tracking) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    const deltaY = touch.clientY - startY;
    const deltaX = Math.abs(touch.clientX - startX);
    if (deltaY > 14 && deltaY > deltaX) {
      event.preventDefault();
    }
  };

  const onTouchEnd = (event) => {
    if (!tracking) return;
    const touch = event.changedTouches?.[0];
    tracking = false;
    if (!touch) return;
    const deltaY = touch.clientY - startY;
    const deltaX = Math.abs(touch.clientX - startX);
    if (deltaY > 54 && deltaY > deltaX) {
      closeMobileSidebar();
    }
  };

  sidebarEl.addEventListener("touchstart", onTouchStart, { passive: true });
  sidebarEl.addEventListener("touchmove", onTouchMove, { passive: false });
  sidebarEl.addEventListener("touchend", onTouchEnd, { passive: true });
}

function updateMobilePanelToggleUi() {
  if (!mobilePanelToggle) return;
  const isMobile = MOBILE_QUERY.matches;
  const isOpen = document.body.classList.contains("sidebar-open");
  const shouldHide = !isMobile || isOpen;
  mobilePanelToggle.classList.toggle("is-hidden-mobile-tab", shouldHide);
  mobilePanelToggle.setAttribute("aria-hidden", String(shouldHide));
  mobilePanelToggle.setAttribute("aria-expanded", String(isOpen));
  mobilePanelToggle.setAttribute("aria-label", isOpen ? "Menu expanded" : "Expand menu");
  mobilePanelToggle.title = isOpen ? "Menu expanded" : "Expand menu";
}

function toggleMobileSidebar() {
  document.body.classList.toggle("sidebar-open");
  updateMobilePanelToggleUi();
}

function closeMobileSidebar() {
  document.body.classList.remove("sidebar-open");
  updateMobilePanelToggleUi();
}

function formatHours(mask, opensAt, closesAt) {
  return `${formatDayMask(Number(mask))} · ${formatMinutes(Number(opensAt))}–${formatMinutes(Number(closesAt))}`;
}

function formatDayMask(mask) {
  const bitToLabel = [
    [1, "Mon"],
    [2, "Tue"],
    [4, "Wed"],
    [8, "Thu"],
    [16, "Fri"],
    [32, "Sat"],
    [64, "Sun"],
  ];

  const labels = bitToLabel.filter(([bit]) => (mask & bit) !== 0).map(([, label]) => label);
  return labels.length ? labels.join(", ") : "Closed";
}

function formatMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function getClosingHourLabel(closesAt) {
  if (closesAt == null || Number.isNaN(Number(closesAt))) {
    return "";
  }
  return String(Math.floor(Number(closesAt) / 60)).padStart(2, "0");
}

function getCityTimeInfo(cityName, dayOffset = 0) {
  const timeZone = state.cityMetaByName.get(cityName)?.timeZone || DEFAULT_CITY_TIME_ZONE;
  const date = new Date(Date.now() + dayOffset * 24 * 60 * 60 * 1000);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  const hour = Number(parts.hour || 0);
  const minute = Number(parts.minute || 0);
  return {
    weekday: parts.weekday,
    weekdayBit: WEEKDAY_BITS[parts.weekday] || 0,
    hour,
    minute,
    minutes: (hour * 60) + minute,
    timeZone,
  };
}

function updateActiveHoursSummary() {
  const label = HOURS_FILTERS[state.scheduleFilter]?.label || HOURS_FILTERS.all.label;

  if (!state.activeCity) {
    activeHoursText.textContent = `${label} · no city loaded`;
    return;
  }

  const timeInfo = state.currentCityClock || getCityTimeInfo(state.activeCity);

  if (state.scheduleFilter === "all") {
    activeHoursText.textContent = `${label} in ${state.activeCity}`;
    return;
  }

  if (state.scheduleFilter === "now") {
    activeHoursText.textContent = `${label}: ${timeInfo.weekday} ${String(timeInfo.hour).padStart(2, "0")}:${String(timeInfo.minute).padStart(2, "0")} (${state.activeCity})`;
    return;
  }

  activeHoursText.textContent = `${label}: ${timeInfo.weekday} (${state.activeCity})`;
}

function buildResultsStatus(count) {
  const filterLabel = HOURS_FILTERS[state.scheduleFilter]?.label || HOURS_FILTERS.all.label;
  if (state.scheduleFilter === "today") {
    return `Showing ${count} places in ${state.activeCity}. Marker badges show closing hour.`;
  }
  if (count === 0) {
    return `No places match the current filters in ${state.activeCity}.`;
  }
  return `Showing ${count} places in ${state.activeCity} · ${filterLabel}`;
}

function rowsFromExec(execResult) {
  if (!Array.isArray(execResult) || execResult.length === 0) {
    return [];
  }

  if (!("columns" in execResult[0]) && !("values" in execResult[0])) {
    return execResult;
  }

  return execResult.flatMap((table) =>
    table.values.map((valueRow) =>
      Object.fromEntries(table.columns.map((columnName, index) => [columnName, valueRow[index]])),
    ),
  );
}

function setStatus(message) {
  if (statusTextEl) {
    statusTextEl.textContent = message;
  }
}

async function updateBytesRead() {
  if (!bytesReadEl) {
    return;
  }
  if (state.localRows) {
    if (bytesReadEl) bytesReadEl.textContent = "Local";
    return;
  }
  if (state.backendMode === "sqlite-http") {
    bytesReadEl.textContent = "Remote";
    return;
  }
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`;
}

function distanceBetween(lat1, lon1, lat2, lon2) {
  const radius = 6371000;
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const dPhi = (lat2 - lat1) * Math.PI / 180;
  const dLambda = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return 2 * radius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistanceMeters(meters) {
  if (meters < 1000) return `${Math.round(meters)} m away`;
  return `${(meters / 1000).toFixed(1)} km away`;
}

function debounce(fn, delay) {
  let timeoutId;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), delay);
  };
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
